// @ts-nocheck — browser module; window globals & untyped DOM refs are expected
/**
 * Podstow — settings view (/settings) entry point
 *
 * Owns everything that lives on the dedicated settings page:
 *   - Subscription summary (tier, usage bar, manage-subscription link)
 *   - Podcast feed URL (input + Copy + QR + mobile app buttons)
 *   - AI assistants (MCP server URL)
 *   - Connected apps (list + revoke)
 *   - Voices (per-voice + region quick-select)
 *   - Feedback (suggestion submit)
 *   - Sign out
 *
 * Auth gating: the inline script in settings/index.html toggles a
 * `.ps-authed` class on <html> so the sign-in card and the settings shell
 * trade places pre-paint based on the token state in localStorage.  When
 * logged out we mount the shared auth UI into #signin-card and skip the
 * data loads; on a successful sign-in we reload so the boot flow re-runs
 * in the logged-in branch.  The onAuthChange listener handles the logout
 * transition by bouncing back to / (the canonical "you've signed out"
 * landing).
 */

import { apiRequest, _setRetryDelays } from './shared/api.js';
import { showToast, _setToastStack } from './shared/toast.js';
import {
  logout, isLoggedIn, getToken, onAuthChange, isAdmin, getUserSub, hasWebkitConfigHandler,
} from './shared/auth.js';
import { initAuthUI } from './shared/auth-ui.js';
import { stashAuthReturn, clearAuthReturn } from './shared/oauth-return.js';
import { safeStorage } from './shared/safe-storage.js';
import {
  isGrandfatheredTier,
  GRANDFATHERED_PLAN_NOTE,
  createIntervalGrouping,
  renderTierCards as renderTierCardsShared,
  tierBadgeClass,
  tierDisplayName,
  tierFeatureLines,
  tierInterval,
  getAllTiers,
  planOffer,
} from './shared/tiers.js';
import {
  applyPurchaseOrder,
  getAllPacks,
  packAppleProductId,
  packGoogleProductId,
  packCardHeading,
  packCardLines,
  packCardNote,
  packPriceLabel,
  packsForTier,
  renderPackCards as renderPackCardsShared,
  PACK_CTA_LABEL,
} from './shared/packs.js';
import { billingView, deriveBilling, PORTAL_DEEP_LINK_PARAM } from './shared/billing.js';
import {
  appleBiller,
  finishAppleTransaction,
  hasAppleIapBridge,
  purchaseAppleProduct,
  openAppleManageSubscriptions,
  onUnsolicitedAppleTransaction,
} from './shared/apple-iap.js';
import {
  describePlayBilling,
  forgetMissingPlayBillingService,
  getPlayBillingService,
  listPlayPurchases,
  playManageSubscriptionsUrl,
  playBiller,
  purchasePlayProduct,
  PLAY_REPLACEMENT_DEFERRED,
} from './shared/google-play-billing.js';
import {
  emitEvent,
  emitPricingView,
  emitUpgradeIntent,
  UPGRADE_SURFACE_SETTINGS,
} from './shared/analytics.js';
import { renderSubscribeBlock } from './shared/onboarding/subscribe-block.js';
import { SAVE_METHODS } from './shared/onboarding/save-methods.js';
import { buildSubscribeQrUrl, showsScanQr } from './shared/onboarding/apps.js';
import { buildQrBlock } from './shared/qr-block.js';
import { deriveUsage, formatPeriodReset } from './shared/usage.js';
import { flashCopied, buildCopyField } from './shared/copy-button.js';
import { shareLink, SHARE_ICON_SVG } from './shared/share-link.js';
import { cursorInstallHref, vscodeInstallHref } from './shared/mcp-clients.js';
import { markTwaSession, markTwaFromReferrer } from './shared/twa-marker.js';
// Which shell hosts this document, and the latch that says its biller priced a
// pack. One home for both, shared with app.js, so the pack card below and the
// wall's refusal copy cannot answer the purchase-door question differently
// (#3416): the card sets the latch, the wall reads it. The card's first render
// also takes the store answer the latch's warm-up already fetched (#3818).
import { isNativeWebview, notePackPriced, takeWarmedPackProducts } from './shell.js';
import { refreshOnResume } from './account-refresh.js';

// Load-time TWA signal — our own Android app stamps document.referrer with its
// package id when it launches this page as a Trusted Web Activity. Mark the tab
// straight away (before the TWA-settle gate below reads the marker) so a
// deep-linked /settings whose CCT port arrives late doesn't briefly paint the
// web upgrade cards the Play Store ToS forbid in-app. Only OUR package matches
// (shared/twa-marker.js), so a third-party Custom Tab can't false-flag.
if (typeof document !== 'undefined') markTwaFromReferrer(document.referrer);

// What account deletion settles, per biller (#3622). `src/account-deletion.ts`
// cancels exactly one: deleteStripeCustomer runs FIRST and aborts the whole
// deletion on failure, so the Stripe sentence is a promise the server keeps.
// Neither store can be cancelled by us — Apple not at all, and Play only
// through a server-side call that would leave one biller settling while the
// other warned, two behaviours for one button — and refusing to delete while a
// store subscription is live collides with App Store Guideline 5.1.1(v) and
// Play's account-deletion policy, the pair #2588 shipped this flow to satisfy.
// So the store arms warn, and deletion proceeds.
//
// One string per provider, rendered in BOTH the lead and the typed
// confirmation: the two cannot drift into disagreeing about the money. Each
// names the store and stops — no link, no host, no purchase verb
// (docs/constraints/billing-store-cards.md's no-steering rule, which binds this copy
// on every surface because a store-billed subscriber reaches this page from a
// plain browser too). Naming the store that bills an existing subscription is
// account management, the same ground the notes above stand on.
//
// The claim is phrased as what deletion does NOT do rather than as an
// instruction, so it stays true for a subscriber already inside the grace
// window: they have cancelled, nothing more will be billed, and "does not
// cancel" is still the fact about deletion. The trailing clause IS an
// instruction, and is the one part that reads oddly there — a grace-window
// subscriber sent to cancel finds it already cancelled. Accepted over a
// fourth, grace-only string: the cost is a wasted look, and one string per
// provider is what stops the lead and the confirmation drifting apart.
const DELETION_BILLING_NOTES = {
  // "Apple App Store", vendor named, for the reason the in-app note carries
  // it: this renders on Android too, where a bare "App Store" makes a reader
  // who switched platforms miss the Apple subscription still billing them.
  apple:  'Deleting your account does not cancel your Apple App Store subscription — cancel it there to stop further billing.',
  google: 'Deleting your account does not cancel your Google Play subscription — cancel it there to stop further billing.',
  stripe: 'An active subscription is cancelled immediately.',
  // /me never arrived, so we cannot name the biller — but the Delete button
  // is live regardless, and silence here is the exact outcome this ticket
  // exists to stop: a store-billed subscriber deleting on the belief that
  // billing stops with the account. Conditional on the reader's own
  // knowledge of where they subscribed, so it stays true for a Stripe or
  // free account that happens to read it, and it promises no cancellation
  // we cannot confirm.
  unknown: 'If you subscribed through the Apple App Store or Google Play, deleting your account does not cancel that subscription — cancel it there to stop further billing.',
};

// ---------------------------------------------------------------------------
// LocalStorage keys
// ---------------------------------------------------------------------------
const LS_FEED_ID = 'podstow_feed_id';
// Admin-only toggle: hide/show the home page's debug panels (the bridge panel and
// the audio trace overlay). Read by app.js on the home page; written here when the
// user flips the switch.
// Absent = default-on (preserves prior behaviour).
const LS_BRIDGE_DEBUG_VISIBLE = 'podstow_bridge_debug_visible';

// ---------------------------------------------------------------------------
// Lazy-load the QRCode library from CDN when needed.
// ---------------------------------------------------------------------------
const QR_CDN = 'https://cdn.jsdelivr.net/npm/qrcode@1.4.4/build/qrcode.min.js';

// Memoised in-flight load: this page has two QR consumers (the save-articles
// web-app row at boot, the feed panel once the feed loads), and without the
// memo the second call re-injects the CDN script while the first is still
// loading. A failed load clears the memo so a later consumer can retry.
let qrLibPromise = null;

function loadQRCode() {
  if (typeof QRCode !== 'undefined') return Promise.resolve(QRCode);
  if (!qrLibPromise) {
    qrLibPromise = injectQRScript().catch((err) => {
      qrLibPromise = null;
      throw err;
    });
  }
  return qrLibPromise;
}

function injectQRScript() {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = QR_CDN;
    s.onload = () => resolve(QRCode);
    s.onerror = () => reject(new Error('QRCode script failed to load'));
    document.head.appendChild(s);
  });
}

// ---------------------------------------------------------------------------
// DOM references (populated after DOMContentLoaded)
// ---------------------------------------------------------------------------
let $feedSubscribe, $feedPlaceholder,
    $logoutBtn,
    $voicesCard, $voiceLang, $voiceList,
    $mcpCard, $mcpUrlInput,
    $appsCard, $appsList, $appsEmpty,
    $subscriptionCard, $subscriptionTierBadge, $manageSubscriptionLink,
    $mobileSubscriptionNote, $mobileSubscriptionText, $mobileSubscriptionLink,
    $usageBarFill, $usageBarExtra, $usageFraction, $usageExtra, $usageReset, $graceNotice,
    $pendingChangeNotice,
    $upgradeCard, $upgradeCardIntro, $upgradeTiers,
    $packCard, $packCardNote, $packCardTrust, $packCardBiller,
    $packCardStatus, $packTiers,
    $packAutoOptInRow, $packAutoOptIn,
    $autoTopUpCard, $autoTopUpState, $autoTopUpOff, $autoTopUpLog,
    $applePurchaseCard, $applePurchaseTiers, $applePurchaseStatus, $applePurchaseNote,
    $playPurchaseCard, $playPurchaseTiers, $playPurchaseStatus, $playPurchaseNote,
    $deleteBillingNote, $deleteStoreWarning;

// TWA settlement — Chrome usually delivers the TWA MessagePort before
// module scripts run, but on a fast /api/me response (or a pull-to-refresh
// / OAuth-redirect reload where Chrome delivers the port late) we can hit
// the first render before isNativeWebview() can return the truth.  That
// briefly paints the upgrade CTA inside the TWA, which the Play Store /
// App Store ToS forbid.  awaitTwaSettled() gates TWA-aware rendering
// until either (a) the port has arrived, (b) the iOS WKWebView handler
// is present, (c) we're on a non-Android UA so no TWA port can be
// coming, or (d) we've waited TWA_SETTLE_TIMEOUT_MS for an Android-UA
// page with no port — at which point we conclude we're in mobile Chrome,
// not in a TWA, and release the upgrade UI.  A real late port arrival
// after that timeout still flips us back via the listener below.
const TWA_SETTLE_TIMEOUT_MS = 500;
let _resolveTwaSettled;
const _twaSettledPromise = new Promise((resolve) => { _resolveTwaSettled = resolve; });
if (isNativeWebview() || !/Android/i.test(navigator.userAgent)) {
  _resolveTwaSettled();
} else {
  setTimeout(_resolveTwaSettled, TWA_SETTLE_TIMEOUT_MS);
}
function awaitTwaSettled() { return _twaSettledPromise; }

// Play Billing availability (#3043, ADR #3041 §2). The Android analogue of
// `hasAppleIapBridge()`, and asynchronous where that one is a property test:
// Chrome answers `getDigitalGoodsService` with a promise, because resolving
// the payment app means binding to it.
//
// Probed once and cached, then AWAITED beside awaitTwaSettled() before the
// in-app parts render — rather than rendering twice. The two answers decide
// the same pixel (which purchase surface, if any, this shell gets), and
// painting the neutral note first and swapping in a purchase card a moment
// later is the flicker awaitTwaSettled() exists to prevent, pointed at the
// same element. The probe is bounded by its own timeout
// (clients/shared/google-play-billing.js), so it cannot hold the card.
let _playBillingAvailable = false;
function probePlayBilling() {
  return getPlayBillingService()
    .then((service) => {
      const available = !!service;
      if (available === _playBillingAvailable) return;
      _playBillingAvailable = available;
      // Same flip-back the late-port listener performs, for the same reason.
      // In practice this never fires a second render: the probe is started at
      // module load and settles long before /me answers, so the first render
      // already sees the truth. On a slow Play Services bind it paints the
      // neutral note first and swaps in the purchase card — which is the SAFE
      // direction of that race. The reverse (a purchase card that turns out
      // not to be backed by a payment app) cannot happen, because the note is
      // what the markup ships.
      if (_lastAccountData) renderInAppDependentParts(_lastAccountData);
    })
    .catch(() => { /* absent is the ordinary answer on every surface but one */ });
}

// Late port arrivals — Chrome usually delivers the port before module
// scripts run, but on pull-to-refresh / OAuth reloads it can land after
// the first render (sometimes even after awaitTwaSettled() has timed out
// on a slow refresh).  When that happens, flip the TWA-aware parts of the
// subscription card so the in-app copy replaces the upgrade button.
let _lastAccountData = null;

/** Bumped by every in-app render; read by the cards after each await. */
let _inAppRenderGeneration = 0;

/** What the last in-app render took the page for: the app, or a browser. */
let _renderedInApp = false;

// The purchase buttons the Play plan ladder currently has on screen (#3910).
// Held rather than re-found because the render that has to dead-end them is the
// one that could NOT rebuild the ladder, so the only handle on them is the one
// the render that built them kept. Emptied wherever the ladder is.
let _playPurchaseButtons = [];

// Whether this document has already reported its Play cards withdrawn (#5414).
// Set by the report, cleared when a Play card prices again: one event per
// withdrawal, however many renders it lasts.
let _playWithdrawalReported = false;

// Started here rather than at first render so its answer is ready before /me
// is: `probePlayBilling` reads `_lastAccountData`, so it cannot run above this
// line, and it must not be deferred to the render either — the whole point is
// that the first paint already knows which purchase surface this shell has.
probePlayBilling();

// Whether the plan cards currently rendered route at the Stripe portal rather
// than at checkout (#3024), which takes their view out of the upgrade funnel
// (#2914). Held at module scope because the nav path reports pricing-views too
// and has no render result to consult.
let _upgradeCardsPortalRouted = false;

// The upsell line the markup ships, remembered on first render so the
// portal-routed copy can be swapped back out again (#3024).
let _upgradeCardUpsell = null;
window.addEventListener('message', (event) => {
  if (event.ports && event.ports.length > 0
      && event.source === null
      && typeof event.origin === 'string'
      && event.origin.startsWith('android-app://')) {
    window._earlyTwaPort = event.ports[0];
    markTwaSession();
    _resolveTwaSettled();
    // The shell asks for the channel again every few seconds for about a
    // minute after this page loads, since nothing on it answers, and Chrome
    // delivers a fresh port each time. Only a render that took the page for a
    // browser has anything to redraw. Redrawing on every port replaced the
    // purchase buttons under a tap, and the tap opened no sheet (#4017).
    // `isNativeWebview()` cannot tell a first port from a repeat here: the
    // inline <head> script has already stored this one.
    if (_lastAccountData && !_renderedInApp) renderInAppDependentParts(_lastAccountData);
  }
});

// ---------------------------------------------------------------------------
// Build the fully-formed RSS feed URL from the current origin
// ---------------------------------------------------------------------------
function refreshFeedUrl() {
  if (!$feedSubscribe) return;
  const feedId = safeStorage.getItem(LS_FEED_ID);

  if (!feedId) {
    $feedSubscribe.hidden = true;
    if ($feedPlaceholder) $feedPlaceholder.hidden = false;
    return;
  }

  // Desktop QR / mobile per-app buttons / feed-URL copy + manual-paste help
  // all come from the shared subscribe component — the same one the SPA's
  // onboarding card and post-save takeover render (issue #1651), so there's
  // one definition and no third copy to drift.
  // The desktop QR encodes the https `open.../subscribe/<feedId>` URL — the
  // static, client-rendered subscribe router page (ADR #2114) the scan resolves
  // to. Falls back to the plain https feed URL when the open domain isn't
  // configured (local dev).
  renderSubscribeBlock($feedSubscribe, {
    feedUrl: `${window.location.origin}/feeds/${feedId}.xml`,
    subscribeUrl: buildSubscribeQrUrl(window.PODSTOW_OPEN_URL, feedId),
    loadQRCode,
    // Success is confirmed in-button by the component's tick swap; only the
    // failure path needs a toast.
    onCopy: (ok) => { if (!ok) showToast('Could not copy.', 'error'); },
  });
  $feedSubscribe.hidden = false;
  if ($feedPlaceholder) $feedPlaceholder.hidden = true;
}

// ---------------------------------------------------------------------------
// A session that has gone, and the page's one response to it (#3911)
// ---------------------------------------------------------------------------
// Every control on this page needs a token: the voice list, the connected
// apps, the MCP URL, account deletion, and — where it costs money — the Buy
// buttons. So `getToken()` answering falsy is never one control's problem, and
// a page that keeps painting over it offers a purchase it cannot credit: the
// store sheet takes the money, the credit posts `Bearer null`, and the
// customer waits days for the store's automatic refund of a purchase that
// should never have started.
//
// What the page shows instead is what it already shows a signed-out visitor.
// True whether or not this is a store shell, so it is stated once here rather
// than per card.
let _signedOut = false;

/**
 * Replace the page with the sign-in card. Reached from the unauthed boot and
 * from any control that discovers the session has gone mid-visit, so a page
 * that loses its session ends where a signed-out visitor starts.
 */
function showSignInCard() {
  if (_signedOut) return;
  _signedOut = true;
  // `html.ps-authed` is the pre-paint gate in settings/index.html: it shows the
  // settings shell and hides the sign-in card, so dropping it swaps the two and
  // takes every control with it, not just the one that asked. At boot the class
  // was never added and this is a no-op.
  document.documentElement?.classList?.remove('ps-authed');
  // …and the shell goes back to its root view, because on a phone a drilled-into
  // section IS the screen: `body:has(.settings-shell[data-view="section"])`
  // hides the app header (style.css), and `:has` goes on matching a shell the
  // line above has just hidden. Left alone, a user who drilled into Subscription
  // to tap Buy would land on a sign-in card with no wordmark and no way home —
  // the dead end settings/index.html's first frame refuses to create at boot by
  // leaving the markup's list view alone whenever `ps-authed` is absent. Reached
  // here whether or not an auth-change reload is also on its way: a session that
  // was already gone when the page asked (cleared in another tab, an expiry with
  // no refresh token) fires no such event, so this is the only thing that moves.
  const shell = document.getElementById('settings-shell');
  if (shell) shell.dataset.view = 'list';
  // Stashed whether or not the card mounts: a federated sign-in round-trips
  // through Cognito's "/" redirect URI, and podstow_next is what lands the user
  // back on this page rather than at the SPA home.
  stashAuthReturn();
  const signinCard = document.getElementById('signin-card');
  if (!signinCard) return;
  initAuthUI({
    container: signinCard,
    showToast,
    // Email/password sign-in never leaves the page, so re-enter the boot flow
    // in its logged-in branch and hydrate the shell normally.
    onSignedIn: () => {
      clearAuthReturn();
      location.reload();
    },
  });
}

/**
 * The token every control on this page reaches the session through.
 *
 * `getToken()` answers falsy two ways, and they are opposite facts.  A session
 * that has GONE leaves nothing to authenticate with — no access token, and no
 * refresh token the IdP will still honour, since a refresh it rejects now drops
 * the local session (clients/shared/auth.js) — and that page is over, so it
 * says so.  A refresh that merely FAILED — a dropped connection, a throttle —
 * leaves a session that is still good and a next tap that would succeed, and
 * bouncing that user to sign-in is worse than leaving them where they are: they
 * keep the page, and the control tells them to try again.
 *
 * `isLoggedIn()` is the separator because it reads what is in storage rather
 * than the result of using it.
 */
async function sessionToken() {
  const token = await getToken();
  if (!token && !isLoggedIn()) showSignInCard();
  return token;
}

/** Store purchases between the tap and their last await; see `runPurchase`. */
let _purchasesRunning = 0;

/**
 * Run one Buy tap's purchase, counted so the resume re-read holds off.
 * @param {() => Promise<unknown>} purchase
 */
async function runPurchase(purchase) {
  _purchasesRunning += 1;
  try {
    return await purchase();
  } finally {
    _purchasesRunning -= 1;
  }
}

/**
 * The pre-flight every store purchase runs BEFORE opening the sheet. After it
 * is too late: the sheet takes the money, and a credit that cannot authenticate
 * leaves the customer paid-up and waiting for the store to refund a purchase
 * nothing will ever consume (#3911).
 *
 * The sheet also needs the account the store records: Play's
 * `obfuscatedAccountId` (ADR #5124), Apple's `appAccountToken`. Each door
 * refuses a pack or a subscription whose record names no account, or another
 * one, after the store has taken the money, so no sheet opens without it.
 *
 * The Cognito `sub` when the session can still credit a purchase. Otherwise
 * null, and the page has already said so — it is the sign-in card, or the
 * caller's status line says to try again — and the caller must not open the
 * sheet. `release` gives the button back on that null: an Apple caller
 * passes `releaseAppleButton`, which keeps it held behind a timed-out purchase.
 */
async function storePayingAccount(btn, setStatus, release = (b) => { b.disabled = false; }) {
  const sub = await sessionToken() ? getUserSub() : null;
  if (sub) return sub;
  release(btn);
  // A session that has gone took the whole shell with it, so there is nothing
  // left on screen to say this to.
  if (!_signedOut) setStatus('Could not confirm your session. Please try again.');
  return null;
}

// ---------------------------------------------------------------------------
// Fetch account info from /me — stores feedId and renders the subscription
// summary.  Called on boot.
// ---------------------------------------------------------------------------
async function fetchAccountInfo() {
  const token = await sessionToken();
  // No token is a read that failed, not a read that did not happen: the page
  // only reaches here signed in, so a session that has since gone is the same
  // dead end as a 500 — and painting nothing left the card claiming `loaded`
  // over figures nobody could refresh (#3901).
  //
  // Unless the session is the thing that went, in which case the page is now
  // the sign-in card and restoring the pack cards under it would put a live
  // Buy button over a session that cannot credit the purchase (#3911).
  if (!token) {
    if (!_signedOut) paintAccountReadFailure();
    return;
  }

  try {
    const res = await apiRequest('/api/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();

    if (data.feedId) {
      safeStorage.setItem(LS_FEED_ID, data.feedId);
    }
    renderSubscriptionCard(data);
    // Deliberately NOT inside renderSubscriptionCard: that function returns
    // early when the subscription card's own refs are absent, and the
    // deletion copy must not inherit that fate — it would silently fall back
    // to saying nothing about a subscription that keeps billing.
    renderAccountDeletionCopy(data);
    refreshFeedUrl();
  } catch (err) {
    paintAccountReadFailure();
    // 4xx — nothing to do (auto-provision via #310 means 403s shouldn't occur)
    if (err?.status >= 400 && err?.status < 500) return;
    showToast('Could not load your account.', 'error');
  }
}

// A page that has shown an account re-reads it when it comes back, the home
// page's code (clients/web/public/account-refresh.js): a slow Play payment is
// credited by Play's notification, often while the app is away (ADR #5124).
// Never before the first read, nor over the sign-in card, nor while a store
// purchase runs: its sheet closing is a focus regain, and the re-render would
// rebuild the cards under the confirm, wiping its status and re-arming Buy.
//
// A Digital Goods lookup that found no service is asked again first (#5414):
// it was cached for the document's life, and a TWA document lives for hours
// across backgrounds. The probe re-renders if the answer flips.
refreshOnResume(() => {
  if (forgetMissingPlayBillingService()) probePlayBilling();
  return _lastAccountData && !_signedOut && _purchasesRunning === 0
    ? fetchAccountInfo()
    : undefined;
});

// What a failed account read leaves on screen. One function for both ways of
// failing — no token, and a read that threw — because the token-less arm
// returned before painting anything at all and so inherited none of this.
function paintAccountReadFailure() {
  // Flip the always-visible card from spinner to inline error so the
  // sidebar entry doesn't lead to a section that spins forever.
  if ($subscriptionCard) $subscriptionCard.dataset.state = 'error';
  // The deletion copy cannot stay silent on a failed read: the Delete
  // button is live either way (#3622).
  renderAccountDeletionCopy(null, { billingUnknown: true });
  // Re-render the store-dependent cards from the last read that worked
  // (#3901). Every purchase flow on this page disables its own button and
  // hands the re-enable to the account read that follows — `fetchAccountInfo`
  // re-renders the card, button and all — so a read that fails strands the
  // button on the surface the customer is trying to buy from, with no way back
  // short of a reload. The articles were credited; only the page is stuck.
  //
  // From `_lastAccountData` rather than nothing, because that is what is
  // already painted: the facts have not moved, only our ability to re-read
  // them, and the subscription card above says so. Before the first successful
  // read there is nothing to restore and no card to strand.
  //
  // `stale`, because that row is not good enough for every card on it — see
  // the flag where `renderInAppDependentParts` reads it.
  if (_lastAccountData) renderInAppDependentParts(_lastAccountData, { stale: true });
}

// ---------------------------------------------------------------------------
// Subscription summary — current tier badge plus an outbound link for
// plan changes, routed by billing provider: Stripe-billed users go to
// /billing, Apple-billed users to Apple's own management surface (there
// is no portal of ours for them — ADR #2867 §5).  Inside a mobile app
// shell (App Store / Play Store ToS — no in-app links to external
// payment flows) the link is swapped for an explanatory note.  In-app,
// every tier gets the same neutral "managed …" sentence with no link, no
// host name, and no purchase language (Guideline 3.1.1 / 3.1.3, #2590
// and the Jul–Aug 2026 rejections) — /billing/ renders tier cards beside
// the Stripe portal button, so no pointer at it counts as mere account
// management. The sentence names the store that actually bills the
// account; naming it is account management, not steering. A shell's one
// route to a Stripe subscription is the manage-only portal (ADR #4555,
// manageStripeFromShell), which cannot sell.
// ---------------------------------------------------------------------------
function renderSubscriptionCard(data) {
  if (!$subscriptionCard || !$subscriptionTierBadge) return;
  _lastAccountData = data;

  // Name and badge style both come from the injected tier config
  // (config/tiers.json via shared/tiers.js) — no hand-maintained map.
  const tier = data?.tier ?? 'free';
  $subscriptionTierBadge.textContent = tierDisplayName(tier);
  $subscriptionTierBadge.className = tierBadgeClass(tier);

  // Article usage bar — rendered proactively so users can see their
  // progress before they hit the cap. The track represents total capacity
  // (allowance + extra); the amber fill is the allowance used, and a green
  // segment pinned to the right shows the durable extra balance (#1771). All
  // figures come from the shared deriveUsage() so this bar and the home
  // screen's out-of-quota Save card agree on what "blocked" means.
  const usage = data?.usage ?? {};
  const limits = data?.limits ?? {};
  const u = deriveUsage(data);
  if ($usageBarFill) {
    $usageBarFill.style.width = `${u.usedPct.toFixed(1)}%`;
    $usageBarFill.classList.remove('full');
    // Two states only: brand amber for usage, red when the user is genuinely
    // out — allowance fully used AND no extra left to fall back on. (No amber
    // "running low" step: green is taken by the extra segment so a distinct
    // mid-step isn't achievable on-brand, and approaching the limit is a state
    // nothing draws attention to — PRD #534, #3443.)
    if (u.blocked) $usageBarFill.classList.add('full');
  }
  if ($usageBarExtra) {
    if (u.hasExtra) {
      $usageBarExtra.style.width = `${u.extraPct.toFixed(1)}%`;
      $usageBarExtra.hidden = false;
    } else {
      $usageBarExtra.hidden = true;
    }
  }
  if ($usageFraction) {
    // The bar's end shows the allowance fraction; the extra sits next to it
    // in green (#usage-extra) — the colour distinguishes it from the fraction,
    // so the two need no separator to stay legible (#1787).
    $usageFraction.textContent = `${u.used.toLocaleString()} / ${u.limit.toLocaleString()}`;
  }
  if ($usageExtra) {
    if (u.hasExtra) {
      $usageExtra.textContent = `+${u.overage.toLocaleString()} extra`;
      $usageExtra.hidden = false;
    } else {
      $usageExtra.hidden = true;
    }
  }
  // Reset date — server enforces the period at midnight UTC
  // (see isPeriodExpired in tier-defaults.ts), so show that instant in local
  // time. Shared with the home screen's out-of-quota Save card via
  // formatPeriodReset so the two surfaces format the same reset instant
  // identically (the card omits the clock time; here we keep it).
  if ($usageReset) {
    $usageReset.textContent = formatPeriodReset(usage, limits, { withTime: true });
  }

  // The two subscription notices, billingView's copy (#4950). Both are
  // "something already decided happens at the end of this period"; they are
  // mutually exclusive by construction, because the shared derivation
  // suppresses the pending change inside the grace window (cancellation wins
  // — see shared/billing.js). Painted here, before the shell has settled,
  // because neither reads a host fact: the view is asked with none.
  //
  // The grace notice: cancelled but paid until period end. The pending one
  // (#3029): a downgrade the user already made at their provider, which takes
  // effect at the end of the period they have paid for. Without it the tap
  // reads as having done nothing: the tier badge correctly still says
  // Premium, and nothing else on the page moves.
  const { graceNotice, pendingNotice } = billingView(data, { app: 'settings' });
  if ($graceNotice) {
    $graceNotice.textContent = graceNotice ?? '';
    $graceNotice.hidden = graceNotice === null;
  }
  if ($pendingChangeNotice) {
    $pendingChangeNotice.textContent = pendingNotice ?? '';
    $pendingChangeNotice.hidden = pendingNotice === null;
  }

  $subscriptionCard.dataset.state = 'loaded';

  // Defer the TWA-aware parts of the card (upgrade button, mobile note,
  // upgrade tier-picker card) until the TWA bridge has settled — see
  // awaitTwaSettled().  The button and upgrade-card both start hidden in
  // HTML, so until settlement they stay safely hidden inside a TWA.
  awaitTwaSettled().then(() => renderInAppDependentParts(data));
}

// Renders the parts of the subscription summary whose visibility depends
// on whether we're inside a native app shell.  Split out of
// renderSubscriptionCard so the eager (data-only) render doesn't have to
// wait on awaitTwaSettled().  Called once on first paint after
// settlement, and again on late TWA port arrival (see message listener
// above) to flip the UI back to in-app copy.
//
// `stale` marks a repaint from the last read that WORKED rather than from a
// fresh one (`paintAccountReadFailure`). It restores the PACK cards and the
// PLAY plan card, and leaves the Apple one exactly as the last render left it.
//
// A pack is a consumable: the tier it was bought on is not what the tap buys,
// so a stale row can still offer one. A plan card is only as safe as its
// acquisition-vs-plan-change decision, because the read that failed is the read
// that would have said the customer now holds a store subscription — and an
// acquisition offered to someone who does opens a SECOND subscription at the
// store's own sheet, which the server refuses only after the money is taken
// (docs/constraints/billing-store-google-cards.md § Do not).
//
// The Play card decides that from Play rather than from the row (#3910): it
// asks `listPurchases()` on every render, so a repaint from the pre-purchase
// row still comes back a plan picker carrying the token the customer now
// holds. Its ladder is therefore safe to rebuild WHERE PLAY ANSWERED, and
// rebuilding it is the only thing that disarms the rungs the failed read left
// live. Where Play answered nothing there is no safe source for either half,
// and the card is left standing untouched instead.
//
// The Apple card has no such source — StoreKit's own subscription group is what
// turns a second purchase there into a plan change, and what the CARD offers
// keys on the row's biller — so it is left standing rather than
// repainted. Left standing rather than withdrawn, in either case: a card
// swapped for the note is a purchase surface that turns out never to have
// existed, which is the one direction docs/constraints/billing-store-cards.md
// rules out.
function renderInAppDependentParts(data, { stale = false } = {}) {
  // Which render of the in-app parts is the live one (ADR #3812).
  //
  // This page legitimately renders more than once — the availability probe
  // flips and re-renders, a late TWA port arrives — and every purchase card
  // below decides its own VISIBILITY after the store answers, not before. So
  // an earlier render resolving last could raise a card a later one had
  // already withdrawn, or paint prices from the earlier catalogue over the
  // later one's.
  //
  // ONE counter for the whole render rather than one per card, because the
  // later render need not call the same renderer: a Stripe-billed account
  // takes no plan card at all, which a per-card counter would never bump,
  // leaving the stale render free to paint.
  _inAppRenderGeneration += 1;

  const tier = data?.tier ?? 'free';
  const inApp = isNativeWebview();
  _renderedInApp = inApp;
  // What this account gets here — the doors, the manage route, the note — is
  // billingView's answer (clients/shared/billing.js), shared with /billing
  // (#4949, #4950). The host facts go in as separate inputs, as
  // docs/constraints/billing-store-cards.md keeps them: `inApp` is the
  // web-host predicate, the Apple shell is its config handler, and each
  // biller answers for itself (the StoreKit bridge, the Digital Goods probe).
  //
  // Below, every element the view decides is set from it on every render,
  // rather than arm by arm: a render follows another for ordinary reasons (an
  // in-place sign-in, a lapse the next read reports, the manage-sheet refresh
  // loop), and an arm that had to undo what another set is the reset five
  // fixes in September were about.
  // The Play cards this render starts, for `reportPlayWithdrawal`.
  const playCards = {};

  const view = billingView(data, {
    app: 'settings',
    inApp,
    appleShell: hasWebkitConfigHandler(),
    storeKit: hasAppleIapBridge(),
    play: _playBillingAvailable,
  });

  // A render inside a shell has to take down what an earlier web render of
  // this page put up — the opt-in included, which the store pack renderers
  // would otherwise leave on their own cards.
  renderAutoTopUpCard(data, inApp);
  if (inApp) renderAutoTopUpOptIn(0);

  applyManageRow(view.manage);

  // The plan door. A store's card goes up over the plan-location note, and
  // only while no card is up: a note replaced by a card is a beat of honest
  // copy, a card replaced by a note was a purchase surface that never existed
  // (ADR #3812). On a cold render that is always — the card ships hidden — but
  // on a re-render the card the last render painted is still on screen, and
  // raising the note beside it is the "both" the pairing rule forbids, for the
  // length of a store round trip. Every arm that withdraws a card raises the
  // note itself, so the other half — never neither — still holds.
  //
  // A store sells a plan only to an account it could bill: acquisition, or a
  // plan change on its own subscription (ADR #2867 §1). Anyone else is
  // already paying elsewhere, and the cross-provider guard would refuse the
  // activation after the store took the money; they keep the note.
  if (view.planDoor === 'apple') {
    if ($playPurchaseCard) $playPurchaseCard.hidden = true;
    if ($applePurchaseCard?.hidden !== false) showPlanLocationNote(view.note);
    // The Apple card has no source but the row for what it offers — StoreKit's
    // own subscription group is what turns a second purchase there into a plan
    // change — so a stale repaint leaves it standing as the last render left
    // it (see the note above this function).
    if (!stale) renderApplePurchaseCard(tier, view.cancelled, view.note);
  } else if (view.planDoor === 'play') {
    if ($applePurchaseCard) $applePurchaseCard.hidden = true;
    if ($playPurchaseCard?.hidden !== false) showPlanLocationNote(view.note);
    // Repainted on a stale row too, unlike the Apple card: this card asks
    // Play what the device holds, so the row it is handed decides no rung's
    // behaviour (#3910). `stale` still rides along, because a repaint Play
    // does not answer has nothing to build from — see the note above.
    playCards.plan = renderPlayPurchaseCard(tier, deriveBilling(data).googleBilled, view.note, { stale });
  } else {
    if ($applePurchaseCard) $applePurchaseCard.hidden = true;
    if ($playPurchaseCard) $playPurchaseCard.hidden = true;
    if ($packCardTrust) $packCardTrust.hidden = true;
    // No store card to cover it: the note stands alone. In a shell whose
    // biller cannot answer it says where the plan lives, with no host, link
    // or purchase language (App Store 3.1.1 and 3.1.3, Play's payments
    // policy; #2590). In a browser it says why a store subscriber has no tier
    // cards. A free account is told nothing.
    showPlanLocationNote(view.note);
  }

  // The Stripe tier cards, which only the plain web's plan door reaches.
  renderUpgradeCard(tier, view);

  // The pack door is the SURFACE's biller, never the account's (ADR #3270):
  // an Apple-billed user in a browser buys on the web, a Stripe-billed one in
  // a shell from the store. A store renderer answers whether it has a door by
  // asking its biller for a price, and LATCHES the answer for the wall
  // (`notePackPriced`, #3416) — gating it on `hasPackDoor()` would be
  // circular. A shell whose biller cannot answer keeps the suppression and
  // never falls through to the Stripe cards (billing-store-cards.md).
  if (view.packDoor === 'play') playCards.pack = renderPlayPackCard(tier);
  else if (view.packDoor === 'apple') renderApplePackCard(tier);
  else renderPackCard(tier, /* suppress */ view.packDoor === 'none');

  if (playCards.plan || playCards.pack) reportPlayWithdrawal(_inAppRenderGeneration, playCards);
}

/**
 * Report a Play card withdrawn because Play priced nothing (#5414).
 *
 * The cards collapse every Play failure into one answer, nothing to sell, and
 * that is right for the cards (ADR #3812). It also left a Subscription page
 * with nothing on it and no record anywhere of why: which call failed, and
 * whether asking a fresh service helped, was readable only on the device's
 * debug panel. So once the render's Play cards have settled, a card Play was
 * asked for and priced nothing sends `describePlayBilling()`'s record to the
 * diagnostics group.
 *
 * Once per withdrawal: a resumed page re-renders on every focus and
 * visibility regain, and the same fault is one report. A card that prices
 * again re-arms it. A report, never a gate — nothing on screen reads it.
 */
async function reportPlayWithdrawal(generation, { plan, pack }) {
  const [planOutcome, packOutcome] = await Promise.all([plan ?? null, pack ?? null]);
  if (generation !== _inAppRenderGeneration) return;
  const outcomes = [planOutcome, packOutcome];
  if (!outcomes.includes('unpriced')) {
    if (outcomes.includes('priced')) _playWithdrawalReported = false;
    return;
  }
  if (_playWithdrawalReported) return;
  _playWithdrawalReported = true;
  const diagnosis = describePlayBilling();
  emitEvent({
    event_type: 'play-billing-diagnostics',
    props: {
      reason: 'play-unpriced',
      play: {
        ...diagnosis,
        plan: planOutcome,
        pack: packOutcome,
        // From the call that priced nothing, not the record's top level: the
        // plan and pack cards load at once, and the last to answer may be the
        // one that priced.
        retried: (diagnosis.lastUnpriced?.attempts ?? 0) > 1 || diagnosis.purchaseAttempts > 1,
      },
    },
  });
}

/**
 * The manage row, from billingView's route (docs/constraints/billing-store-manage.md).
 *
 * One element across renders, every attribute written every time: an href, a
 * target or a tap route an earlier render set must not survive into this one.
 * A shell never keeps the markup's /billing href — a hidden row still carries
 * one to long-press and copy-link (docs/constraints/billing-store-cards.md) —
 * which billingView guarantees by never answering 'billing' inside one.
 *
 * Routes with an href are plain links: /billing in a browser, Apple's and
 * Play's own subscriptions pages (ordinary URLs the store apps claim, so
 * copy-link lands where the tap does). The two without one are taps, served by
 * the one listener `bindManageRow` binds: Apple's sheet in the Apple shell, and
 * the manage-only portal session for a Stripe subscriber in either shell
 * (ADR #4555), whose URL exists only after an authenticated request.
 *
 * @param {import('./shared/billing.js').ManageRoute | null} manage
 */
function applyManageRow(manage) {
  _manageRowRoute = manage?.route ?? null;
  if (!$manageSubscriptionLink) return;
  const href = manage?.route === 'play-page'
    ? playManageSubscriptionsUrl(manage.productId ?? '')
    : manage?.href ?? null;
  if (href) $manageSubscriptionLink.href = href;
  else $manageSubscriptionLink.removeAttribute('href');
  // Another host's page opens outside this one; /billing is this site.
  if (href && manage?.route !== 'billing') {
    $manageSubscriptionLink.target = '_blank';
    $manageSubscriptionLink.rel = 'noopener noreferrer';
  } else {
    $manageSubscriptionLink.removeAttribute('target');
    $manageSubscriptionLink.removeAttribute('rel');
  }
  $manageSubscriptionLink.hidden = manage === null;
  if (manage) {
    $manageSubscriptionLink.textContent = manage.label;
    // data-action lets style.css hide the link on desktop only when it's the
    // redundant 'Upgrade plan' twin of the inline tier picker. Paid users
    // keep their 'Manage subscription' link regardless — it's their route to
    // invoices and cancellation.
    $manageSubscriptionLink.dataset.action = manage.action;
  }
  if (_manageRowRoute === 'apple-sheet' || _manageRowRoute === 'portal-shell') bindManageRow();
}

// ---------------------------------------------------------------------------
// Refresh after Apple's manage-subscriptions sheet closes.
// ---------------------------------------------------------------------------
// A cancel (or un-cancel, or plan change) made in the sheet reaches our
// server via the App Store Server Notification, which can lag the user
// dismissing the sheet. Dismissal therefore asks the server to reconcile
// from Apple directly (reconcileSubscription below), then re-reads on this
// one loop until the billing facts move or the delays run out (the user may
// have changed nothing at all). A successful reconcile just means the first
// read already shows the change and the loop stops there.
const APPLE_MANAGE_REFRESH_DELAYS_MS = [3000, 8000];
let appleManageRefreshDelaysMs = APPLE_MANAGE_REFRESH_DELAYS_MS;

// The facts whose movement means "the sheet did something" — the fields the
// subscription card is rendered from, not the whole /me payload (usage can
// tick over mid-sheet without meaning anything happened in it).
function billingFactsSnapshot(data) {
  return JSON.stringify({
    tier: data?.tier ?? 'free',
    paid: data?.paid === true,
    billingProvider: data?.billingProvider ?? null,
    subscriptionEndsAt: data?.subscriptionEndsAt ?? null,
    // The scheduled plan change (#3029) is a card-rendered fact too, and it
    // is the ONLY one a downgrade taken in the sheet moves — the tier stays
    // put by design. Without it here the refresh loop could never detect that
    // downgrade and would burn its whole ladder every time.
    pendingTier: data?.pendingTier ?? null,
    pendingTierAt: data?.pendingTierAt ?? null,
  });
}

// Ask the server to read the subscription's CURRENT state from the provider
// and apply it — the same synchronous pattern confirm-subscription uses on
// checkout return, pointed at management instead. Best-effort: its outcome
// is deliberately not branched on. The one refresh loop below already stops
// the moment the billing facts move, so a successful reconcile simply makes
// the first read the one that moves them — and a failed (or stale) one
// leaves the loop doing exactly what it did before. Never throws.
async function reconcileSubscription() {
  try {
    const token = await sessionToken();
    if (!token) return;
    await apiRequest('/api/reconcile-subscription', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // Unconfigured stage, transient failure, older deploy — the timed
    // re-reads below carry the refresh either way.
  }
}

async function refreshAfterAppleManage() {
  const before = billingFactsSnapshot(_lastAccountData);
  await openAppleManageSubscriptions();
  await refreshBillingSince(before);
}

// Reconcile, then re-read until the billing facts move off `before` or the
// delays run out. Shared by both management routes a shell opens.
async function refreshBillingSince(before) {
  await reconcileSubscription();
  await fetchAccountInfo();
  for (const delayMs of appleManageRefreshDelaysMs) {
    if (billingFactsSnapshot(_lastAccountData) !== before) return;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    await fetchAccountInfo();
  }
}

// ---------------------------------------------------------------------------
// The manage row's tap, when the row has no href to follow.
// ---------------------------------------------------------------------------
// One listener for every route, bound once: the row is one element across
// renders, and a second listener bound by another route would fire beside
// the first. `_manageRowRoute` is set from billingView's route on every render
// (`applyManageRow`); a route with an href leaves the tap to it.
let _manageRowRoute = null;

function bindManageRow() {
  if (!$manageSubscriptionLink || $manageSubscriptionLink.dataset.manageBound) return;
  $manageSubscriptionLink.dataset.manageBound = '1';
  $manageSubscriptionLink.addEventListener('click', (event) => {
    // Both return their promise for tests; neither rejects.
    if (_manageRowRoute === 'apple-sheet') {
      event.preventDefault();
      // The bridge resolves when the sheet is DISMISSED — the signal to ask
      // the server to reconcile state from Apple and re-read the account.
      return refreshAfterAppleManage();
    }
    if (_manageRowRoute === 'portal-shell') {
      event.preventDefault();
      return manageStripeFromShell();
    }
    return undefined;
  });
}

// ---------------------------------------------------------------------------
// A Stripe subscription managed from a store shell (#4062, ADR #4555).
// ---------------------------------------------------------------------------
// The server mints the session on the manage-only portal configuration
// (`surface: 'shell'`, src/billing/portal-session.ts): cancel, fix the card, see
// invoices, and no plan change. The session URL authenticates on its own,
// which is why it is what opens: the system browser does not share the
// app's sign-in. The session carries no return URL, so the portal links
// back nowhere — the user comes back to the app, and the app reconciles.
let _stripeManageOpening = false;
let _stripeManageReturn = null;

async function createShellPortalSession() {
  try {
    const token = await sessionToken();
    if (!token) return null;
    // Not retried: the link activation below rides the tap's transient
    // activation, which a retry ladder outlasts, so a late success would open
    // nothing. A failure toasts and the user taps again.
    const res = await apiRequest('/api/create-portal-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ surface: 'shell' }),
    }, { retry: false });
    const body = await res.json();
    return typeof body?.url === 'string' && body.url.startsWith('https://') ? body.url : null;
  } catch {
    return null;
  }
}

// A link activation, the one way out of both shells for a URL the page only
// has after an await. The Apple shell hands a link activation to another
// host to Safari (clients/apple/Podstow/ContentView.swift,
// `opensOutsideWebView`); a programmatic `location` change would stay inside
// the web view, and a `_blank` target reaches no UI delegate there. The TWA
// opens a `_blank` link outside itself; the click rides the tap's transient
// activation, which is why the session request is the only await before it.
function openOutsideApp(url) {
  const link = document.createElement('a');
  link.href = url;
  if (!hasWebkitConfigHandler()) {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Resolves the next time the app is in front again: the page becoming
// visible (a phone returning from the browser) or the window regaining
// focus (the Mac app, whose page never hides).
let _stopWaitingForReturn = null;

function nextReturnToApp() {
  return new Promise((resolve) => {
    const back = () => {
      if (document.visibilityState === 'hidden') return;
      _stopWaitingForReturn?.();
      resolve();
    };
    _stopWaitingForReturn = () => {
      window.removeEventListener('focus', back);
      document.removeEventListener('visibilitychange', back);
      _stopWaitingForReturn = null;
    };
    window.addEventListener('focus', back);
    document.addEventListener('visibilitychange', back);
  });
}

async function manageStripeFromShell() {
  if (_stripeManageOpening) return;
  _stripeManageOpening = true;
  let url;
  try {
    url = await createShellPortalSession();
  } finally {
    _stripeManageOpening = false;
  }
  if (!url) {
    // Never a fallback to /billing or the default portal: both sell.
    showToast('Could not open subscription management. Try again.', 'error');
    return;
  }
  // One refresh per trip out, however many taps opened the portal.
  const awaiting = _stripeManageReturn;
  if (!awaiting) {
    const before = billingFactsSnapshot(_lastAccountData);
    _stripeManageReturn = nextReturnToApp();
    openOutsideApp(url);
    await _stripeManageReturn;
    _stripeManageReturn = null;
    await refreshBillingSince(before);
    return;
  }
  openOutsideApp(url);
  await awaiting;
}

// ---------------------------------------------------------------------------
// Native in-app purchase card (#2875, ADR #2867 §2)
// ---------------------------------------------------------------------------
// Rendered only inside an Apple shell that registered the StoreKit bridge.
// Prices are read from StoreKit at runtime, never from config: App Store
// prices are per-territory and Apple can move a price point under us, so the
// number shown must be the number Apple charges. `config/tiers.json`'s Apple
// amount is what the *catalogue* is built from (sync-apple-products.mjs) —
// it is not a display value, and using it here would show a US price to a
// user being charged in their own currency.
//
// Nothing in this card may reference a Stripe price id, /billing, or the web
// purchase host: it is the app's own purchase mechanism, and the moment it
// pointed anywhere else it would become the external-steering call to action
// Guideline 3.1.1 forbids. Pinned by test/spa-settings.test.ts (FF-2).
//
// The card itself is `renderStorePlanCard` over `appleBiller` (#4953).
// `note` is here for one reason: every arm below that withdraws the card has
// to state where the plan lives instead, because the caller no longer raises
// the note over a card a previous render left on screen. Exactly one of the
// two, never both, never neither (docs/constraints/billing-store-cards.md).
async function renderApplePurchaseCard(currentTier, cancelled, note) {
  if (!$applePurchaseCard || !$applePurchaseTiers) return;
  const generation = _inAppRenderGeneration;

  // Tiers ranked strictly above the user's current one, or every Apple tier
  // inside the grace window — the user is reverting to free, so the plans are
  // all relevant again, the one being left included. StoreKit makes
  // re-offering it safe: purchasing a product the user already holds with
  // auto-renew off runs Apple's own resubscribe flow (no second charge), and a
  // different product in the subscription group is a plan change. The rule is
  // planOffer's (clients/shared/tiers.js), which the App Store review capture
  // reads too (#4952).
  const rungs = planOffer({ currentTier, door: 'apple', cancelled });

  // Inside the grace window the card opens on the interval of the plan being
  // left, which is one of the rungs.
  const heldTier = cancelled ? rungs.find((r) => r.tier.id === currentTier)?.tier : undefined;

  // Top of the ladder, a deploy whose tier config carries no Apple products,
  // or a StoreKit that priced nothing: the card withdraws and the note says
  // where the plan lives in its place.
  const buttons = await renderStorePlanCard({
    biller: appleBiller,
    $card: $applePurchaseCard,
    $tiers: $applePurchaseTiers,
    $note: $applePurchaseNote,
    setStatus: setApplePurchaseStatus,
    rungs,
    currentTier,
    preferredInterval: heldTier ? tierInterval(heldTier) : undefined,
    generation,
    withdraw: () => {
      $applePurchaseCard.hidden = true;
      showPlanLocationNote(note);
    },
    // Same StoreKit purchase whatever the CTA says — "Resubscribe" only names
    // what Apple's sheet will actually do for the tier the user still holds.
    buy: ({ tier }, btn) => startApplePurchase(tier, btn),
  });
  if (buttons) {
    _appleCardButtons.plan = buttons;
    holdAppleCardsForTimeout();
  }
}

/**
 * One store plan card, over either biller (#4953): the Apple and the Play
 * purchase cards are this renderer with a different `biller` (`appleBiller`,
 * `playBiller`) and different elements. What differs between the two stores
 * reaches it as inputs — the rungs and what each tap does come from
 * `planOffer` and the caller's `buy`, a Play replacement included; how a card
 * withdraws is the caller's `withdraw` — never as a branch here.
 *
 * Every store-card rule lives here once (docs/constraints/billing-store-cards.md):
 *
 * - Nothing store-dependent paints before the biller answers, the card's own
 *   visibility included (ADR #3812): the displayed price has to be the
 *   store's, and whether there is a card at all depends on the same answer.
 *   Both billers' `load` resolve rather than hang, so the wait is bounded.
 * - Renders race, so the generation is checked after the await and a render a
 *   later one superseded returns null without touching the DOM.
 * - The biller being present is necessary, not sufficient: nothing priced
 *   withdraws the card and leaves the note standing, because a card of dead
 *   buttons reads as a broken app. A PARTIAL catalogue still renders — the
 *   store priced a rung, so the offer is real, and hiding the rest would
 *   silently change the ladder (docs/constraints/tier-ladder.md). Those rungs
 *   say "Unavailable" with a dead button.
 * - The card replaces the note only once it paints; every arm without a card
 *   is `withdraw`, which states where the plan lives.
 * - Display price only, never a configured amount: stores reprice per
 *   territory. Every store string goes in as a text node.
 *
 * Deliberately NOT renderTierCards() from shared/tiers.js, despite the similar
 * markup: that helper builds its card with innerHTML interpolation, and the
 * display price and the period here come from the store rather than our own
 * config.
 *
 * Resolves to the card's buttons once it paints, or null where it withdrew or
 * a later render took over.
 */
async function renderStorePlanCard({
  biller, $card, $tiers, $note, setStatus, rungs, currentTier, preferredInterval,
  generation, withdraw, buy,
}) {
  const purchasable = rungs.map((r) => r.tier);
  if (purchasable.length === 0) {
    withdraw();
    return null;
  }

  const ids = purchasable.map((t) => biller.productIdOf(t));
  const products = await biller.load(ids);
  if (generation !== _inAppRenderGeneration) return null;

  const productOf = (tier) => products[biller.productIdOf(tier)];
  if (!biller.anyPriced(products, ids)) {
    withdraw();
    return null;
  }

  $card.hidden = false;
  // The card is real, so it takes the note's place.
  hidePlanLocationNote();
  setStatus('');

  // The one-way warning a grandfathered plan carries on every purchase
  // surface: the card sells the active set only, so buying leaves the held
  // plan for good. Same keying as the web picker (`paid && !active`) and
  // nothing more — no price, no host, no link
  // (docs/constraints/billing-store-cards.md).
  if ($note) {
    const grandfathered = isGrandfatheredTier(currentTier);
    $note.textContent = grandfathered ? GRANDFATHERED_PLAN_NOTE : '';
    $note.hidden = !grandfathered;
  }

  $tiers.innerHTML = '';

  // A two-interval offer renders behind the shared Monthly/Annual toggle (ADR
  // #3282 §6), exactly as the web picker does — without the "2 months free"
  // flash: the twelve-for-ten arithmetic holds for the web prices only
  // (net-match puts a store's annual slightly above ten monthly renewals, and
  // the stores reprice per territory), and this card must not make a pricing
  // claim the store's own numbers contradict.
  const grouping = createIntervalGrouping(purchasable, { preferredInterval, flash: false });
  if (grouping.toggle) $tiers.appendChild(grouping.toggle);

  const buttons = [];
  for (const rung of rungs) {
    const { tier, cta } = rung;
    const product = productOf(tier);
    const displayPrice = biller.priceOf(product);
    const card = document.createElement('div');
    card.className = `tier${tier.highlight ? ' highlight' : ''}`;
    grouping.register(card, tier);

    const heading = document.createElement('h3');
    // The short heading where the interval is already on screen (the toggle,
    // and the period beside the price); `name` stays self-describing on the
    // surfaces the store renders (its own sheet, receipts).
    heading.textContent = tier.cardName ?? tier.name;
    card.appendChild(heading);

    const price = document.createElement('div');
    price.className = 'price';
    if (displayPrice) {
      price.textContent = displayPrice;
      const interval = document.createElement('span');
      // The store's real billing period, like the price (#3287). Config's
      // interval is the fallback where the store sent none.
      interval.textContent = ` / ${biller.periodOf(product) ?? tier.interval ?? 'month'}`;
      price.appendChild(interval);
    } else {
      // The store could not price the product — usually a catalogue that has
      // not propagated, or a build it does not sell to. Say so rather than
      // inventing a number.
      price.textContent = 'Unavailable';
    }
    card.appendChild(price);

    const features = document.createElement('ul');
    for (const line of tierFeatureLines(tier)) {
      const li = document.createElement('li');
      li.textContent = line;
      features.appendChild(li);
    }
    card.appendChild(features);

    const btn = document.createElement('button');
    btn.className = tier.highlight ? 'btn btn-primary' : 'btn btn-outline';
    // What the tap will do, planOffer's CTA.
    btn.textContent = cta;
    // One CTA per rung, often the same word, so the NAME carries the rung
    // (docs/constraints/accessible-names.md). This card builds its own DOM
    // rather than calling renderTierCards, so it names its buttons itself.
    btn.setAttribute('aria-label', `${btn.textContent}, ${tier.cardName ?? tier.name}`);
    btn.disabled = !displayPrice;
    btn.addEventListener('click', () => runPurchase(() => buy(rung, btn)));
    buttons.push(btn);
    card.appendChild(btn);

    $tiers.appendChild(card);
  }
  grouping.apply();
  return buttons;
}

// Hand a signed StoreKit transaction to the server, which is what actually
// grants the tier. Shared by the purchase flow and the unsolicited-transaction
// path so both reach the server the same way.
async function confirmAppleTransaction(signedTransaction) {
  const token = await sessionToken();
  // Never post without one. A `Bearer null` is answered 401 without the server
  // ever seeing the transaction, so the round trip can only mislead: a
  // credential we could not produce is our end unable to ask, not a verdict on
  // the purchase. The error carries no status, which is the transient shape —
  // the transaction stays unfinished for the next launch (#3911). A 401 the
  // server does answer is read the same way (#3924, `packRefusedForGood`).
  if (!token) throw new Error('Could not confirm your session. Please try again.');
  const res = await apiRequest('/api/apple-purchase', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ signedTransaction }),
  });
  return res.json();
}

// A transaction can land with no purchase behind it: an Ask to Buy approval
// arriving later, a renewal, a purchase made on another device, or an
// unfinished pack consumable StoreKit is re-delivering (#3327). StoreKit
// replays those on launch, and they still have to reach the server —
// otherwise a user who has paid sits on the free tier, or without the articles
// they bought, until Apple's notification happens to arrive.
//
// Failures are swallowed: the App Store Server Notification for the same
// transaction is the authority and will land regardless, so there is nothing to
// tell the user about — and a consumable whose confirm failed stays unfinished,
// so StoreKit simply offers it again next launch.
onUnsolicitedAppleTransaction(async (payload) => {
  try {
    await confirmAppleTransaction(payload.signedTransaction);
    // Only now: an unfinished consumable is the retry mechanism, so finishing
    // one whose credit had not landed would throw the purchase away.
    if (payload.consumable) await finishAppleTransaction(payload.transactionId);
    await fetchAccountInfo();
  } catch (err) {
    // Silent, but not inert. This handler is where a re-delivered consumable
    // lands, so a refusal the server will repeat would otherwise loop here on
    // every launch, forever, against a rate-limited endpoint. Finish it and the
    // loop ends; a transient failure still leaves it unfinished and retried.
    if (payload.consumable && packRefusedForGood(err)) {
      await finishAppleTransaction(payload.transactionId).catch(() => {});
    }
  }
});

function setApplePurchaseStatus(message) {
  if (!$applePurchaseStatus) return;
  $applePurchaseStatus.textContent = message;
  $applePurchaseStatus.hidden = !message;
}

// Reveal the pack trust line, naming the store whose sheet is about to open.
// One helper because the pack section is shared across billers and the name is
// the only thing in it that varies — "Billed by the App Store" over a Play
// purchase is the kind of wrong a store reviewer reads as steering.
function showPackBiller(name) {
  if ($packCardBiller) $packCardBiller.textContent = name;
  if ($packCardTrust) $packCardTrust.hidden = false;
}

// The pack card's own status line (#3327, #3452). Separate from the plan
// card's because the two cards swap order by tier, and a Stripe-billed user in
// the shell is shown no plan card at all — a shared line would land off
// screen, or nowhere, exactly as a purchase outcome arrived. Shared by both
// store doors: there is one pack section, whichever biller fills it.
function setPackCardStatus(message) {
  if (!$packCardStatus) return;
  $packCardStatus.textContent = message;
  $packCardStatus.hidden = !message;
}

// What a StoreKit answer that is not a success says on a card's status line,
// '' for nothing. `outcome` is what the purchase would have done: "Your plan
// updates", "Your articles arrive".
function appleNotOkLine(result, outcome) {
  // A cancel is the commonest non-success outcome and is not a failure —
  // showing it as one reads as a bug in the app.
  if (result.reason === 'cancelled') return '';
  if (result.reason === 'pending') {
    return `Your purchase is waiting for approval. ${outcome} once it goes through.`;
  }
  // A timeout is the bridge giving up, not StoreKit: the purchase can still
  // complete, and its late success is credited unsolicited (apple-iap.js).
  // "Try again" here invites a second charge.
  if (result.reason === 'timeout') {
    return `The App Store hasn't answered yet. ${outcome} if the purchase goes through.`;
  }
  return result.message || "Couldn't complete the purchase. Please try again.";
}

// A purchase the bridge timed out on can still complete, so until StoreKit
// answers no Apple Buy button is live, on either card (#5281): a second tap
// opens a second sheet, and a pack bought twice is charged twice. The resume
// re-read rebuilds both cards, which is exactly when the user is back from
// updating a payment method, so the hold lives here rather than on the
// buttons, and each Apple card render re-applies it.
//
// Each timed-out purchase still out, as { setStatus, line }. Two can be: the
// in-flight one disables only its own button. A shell that never answers holds
// until the page reloads.
const _appleTimeouts = new Set();
/** The Apple cards' current buttons, as each render last built them. */
const _appleCardButtons = { plan: [], pack: [] };
/** Buttons the hold disabled, which are the ones a release gives back. */
let _appleHeldButtons = new WeakSet();

function appleCardButtons() {
  return [..._appleCardButtons.plan, ..._appleCardButtons.pack];
}

function holdAppleCardsForTimeout() {
  if (_appleTimeouts.size === 0) return;
  for (const btn of appleCardButtons()) {
    if (btn.disabled) continue;
    btn.disabled = true;
    _appleHeldButtons.add(btn);
  }
  for (const { setStatus, line } of _appleTimeouts) setStatus(line);
}

function holdAppleCardsUntilAnswered(result, setStatus, outcome) {
  const held = { setStatus, line: appleNotOkLine(result, outcome) };
  _appleTimeouts.add(held);
  holdAppleCardsForTimeout();
  // A late success is credited unsolicited, which re-reads the account; a late
  // cancel or failure reaches nothing else, so its line is said here.
  result.late?.then((answer) => {
    _appleTimeouts.delete(held);
    setStatus(answer?.ok ? '' : appleNotOkLine(answer ?? {}, outcome));
    // Another purchase can still complete: keep holding, and say its line.
    if (_appleTimeouts.size > 0) return holdAppleCardsForTimeout();
    for (const btn of appleCardButtons()) {
      if (_appleHeldButtons.has(btn)) btn.disabled = false;
    }
    _appleHeldButtons = new WeakSet();
  });
}

// Give an Apple purchase's own button back when its purchase ends. The hold
// skipped it while it was in flight (already disabled), so with another
// purchase still out it joins the held set rather than going live.
function releaseAppleButton(btn) {
  if (_appleTimeouts.size === 0) {
    btn.disabled = false;
    return;
  }
  btn.disabled = true;
  _appleHeldButtons.add(btn);
}

// Run one StoreKit purchase and hand the signed transaction to the server.
// The server is what grants the tier — the client never decides it — and the
// App Store Server Notification for the same purchase races this call
// harmlessly: both go through the same guarded activation.
async function startApplePurchase(tier, btn) {
  btn.disabled = true;
  // A pre-flight that fails gives the button back, unless a timed-out purchase
  // still holds it; then the timeout's line, not "try again", is what stands.
  const sub = await storePayingAccount(btn, setApplePurchaseStatus, releaseAppleButton);
  if (!sub) return holdAppleCardsForTimeout();
  setApplePurchaseStatus('Contacting the App Store…');

  const result = await purchaseAppleProduct(tier.apple.productId, sub);

  if (!result.ok) {
    // A timeout takes it back with the rest of the Apple buttons (#5281).
    releaseAppleButton(btn);
    if (result.reason === 'timeout') {
      return holdAppleCardsUntilAnswered(result, setApplePurchaseStatus, 'Your plan updates');
    }
    return setApplePurchaseStatus(appleNotOkLine(result, 'Your plan updates'));
  }

  setApplePurchaseStatus('Confirming your subscription…');
  try {
    const body = await confirmAppleTransaction(result.signedTransaction);
    showToast(`You're on ${tierDisplayName(body.tier)}.`);
    setApplePurchaseStatus('');
    await fetchAccountInfo();
  } catch (err) {
    releaseAppleButton(btn);
    // 409 — the account is already billed elsewhere. Apple has taken the
    // money and we cannot refund it (Apple owns refunds), so name the route
    // that can rather than showing a generic failure.
    //
    // Keyed on the CODE, not the status, exactly as the Play door is: the
    // purchase door answers 409 for a second reason (`subscription_mismatch`,
    // #3668 — its own App Store read could not restamp the record), and the
    // notification stream grants that purchase moments later. Sending that
    // user for a refund would undo the thing they just bought.
    if (err?.status === 409 && errorCodeOf(err) === 'already_subscribed_elsewhere') {
      return setApplePurchaseStatus(
        'This account already has a subscription billed elsewhere, so it was not charged twice here. '
        + 'To undo the App Store charge, request a refund at reportaproblem.apple.com.',
      );
    }
    // The purchase itself succeeded — Apple's notification will grant the
    // tier even though this confirm call failed, so don't imply it was lost.
    setApplePurchaseStatus(
      'Your purchase went through. It can take a moment to appear — refresh if it does not.',
    );
  }
}

// ---------------------------------------------------------------------------
// Play Billing purchase card (#3043, ADR #3041 §2)
// ---------------------------------------------------------------------------
// Rendered only where the Digital Goods service resolves — a Play-installed
// TWA whose shell declared the billing manifest entries.
//
// Prices come from Play's `getDetails()`, never from config: Play reprices per
// region, and the number shown must be the number Play charges.
// `config/tiers.json`'s `google.priceAmountCents` is what the CATALOGUE is
// built from (scripts/sync-google-products.mjs) — it is not a display value,
// and rendering it would show a US price to someone billed in their own
// currency. Pinned by test/spa-settings.test.ts (FF-2).
//
// Nothing here may reference a Stripe price id, /billing, or the web purchase
// host: this is the app's own purchase mechanism, and Play's payments policy
// forbids steering out of it exactly as Guideline 3.1.1 does.
//
// The card itself is `renderStorePlanCard` over `playBiller` (#4953).
// `note` is here for the reason the Apple twin gives: every arm that withdraws
// the card states where the plan lives instead.
//
// Resolves to what became of the card, for `reportPlayWithdrawal`: 'priced',
// 'unpriced' where Play was asked and gave nothing to build from, 'none' where
// there was nothing to ask it, or null where a later render took over.
async function renderPlayPurchaseCard(currentTier, subscriber = false, note = null,
  { stale = false } = {}) {
  if (!$playPurchaseCard || !$playPurchaseTiers) return 'none';
  const generation = _inAppRenderGeneration;

  // Withdraw the card and say where the plan lives instead — every arm below
  // that has nothing to sell.
  //
  // Never on a stale repaint, though: the card standing there is the last good
  // read's, and swapping it for the note is a purchase surface that turns out
  // never to have existed, which is the one direction
  // docs/constraints/billing-store-cards.md rules out. ADR #3812's collapse is
  // about a FRESH answer that priced no rung; a repaint has a card already.
  // What it gets instead is `deadEndPlayLadder`.
  const withdraw = () => {
    if (stale) return deadEndPlayLadder();
    $playPurchaseCard.hidden = true;
    showPlanLocationNote(note);
  };

  // For a live Google subscriber this card is a PLAN PICKER rather than a
  // purchase card (#3045), and the difference it turns on is the purchase
  // token Play holds for the subscription being replaced. `listPurchases()` is
  // the only place a web page can get one: Play's replacement flow carries the
  // OLD token, and the Digital Goods API passes no account id, so there is
  // nothing else to look one up by.
  //
  // Asked of Play rather than of our own record deliberately — it has to be a
  // token the Play account signed in on THIS device holds, which is the
  // account whose sheet is about to open, not the one that happens to be on
  // our row.
  //
  // And asked on EVERY render rather than only where the row says the account
  // is Google-billed (#3910). The row is the half that can be stale: the /me
  // read after a purchase is exactly the one that fails, and gating on it left
  // a customer who now holds a Play subscription looking at an ACQUISITION
  // ladder — `purchasePlayProduct(sku, null)` on every rung, which opens a
  // SECOND subscription at Play's own sheet and is refused by the server only
  // after the money is taken (docs/constraints/billing-store-google-cards.md §
  // Do not). Play's answer cannot be stale in that direction, so the card's
  // mode comes from it alone and is right whatever the row says. The cost is
  // one bounded `listPurchases()` per render of the card.
  const held = await heldPlaySubscription();
  if (generation !== _inAppRenderGeneration) return null;
  // Play's own answer said nothing, as against an answer of "owns nothing".
  const purchasesFailed = !held && describePlayBilling().purchases !== 'answered';
  // A stale repaint is built from Play's answer and from nothing else, so an
  // answer of NOTHING leaves it with nothing to build from. `listPlayPurchases`
  // says empty three ways — a payment app predating Digital Goods v2.0, a call
  // the bound cut off, one that threw — and none of them is "owns nothing"
  // here, because the row cannot be asked either. Rebuilding from that row is
  // the acquisition ladder offered to a subscriber that #3910 is about:
  // `purchasePlayProduct(sku, null)` on the rung they just bought, which opens
  // a SECOND subscription at Play's own sheet and is refused by the server only
  // after the money is taken. So the last good read's card is left standing and
  // its rungs are dead-ended, the tapped one included.
  if (stale && !held) {
    deadEndPlayLadder();
    return purchasesFailed ? 'unpriced' : 'none';
  }
  if (subscriber && !held) {
    // ADR #3041 §5's specified fallback, reached by a real case rather than a
    // hypothetical one: the customer is signed into podstow on a device whose
    // Play account did not buy the subscription. A purchase here would be an
    // acquisition, so it would open a SECOND Play subscription rather than
    // replace the first — the server's same-provider guard refuses it, but
    // only after Play has taken the money. Suppress the cards; the neutral
    // note and the manage link (which reaches Play, where they can still
    // cancel) are what remains, and neither is a dead end.
    // The note says where the plan lives in the card's place.
    withdraw();
    return purchasesFailed ? 'unpriced' : 'none';
  }

  // What the customer is moving FROM, for the interval toggle below. Play's own
  // answer where there is one: our row can lag a plan change by a reconcile.
  // planOffer reads the same answer to leave that plan off the ladder.
  const heldTier = held
    ? getAllTiers().find((t) => t.google?.productId === held.itemId)
    : undefined;
  const fromTier = heldTier ?? getAllTiers().find((t) => t.id === currentTier);

  // Without a held token, the tiers ranked strictly above the user's current
  // one. A live subscriber is offered the whole active ladder instead, minus
  // the plan they hold: a plan change goes in both directions, and which
  // direction it is decides only when it takes effect, never whether it is
  // offered. That is #3024's rule for the Stripe twin, and ADR #3028's
  // one-door model on both.
  //
  // Keyed on the HELD TOKEN rather than on the row (#3910), for the reason the
  // lookup above gives: what every rung will do is decided by whether there is
  // a token to replace, so what is offered has to be decided by the same fact.
  // The rule is planOffer's (clients/shared/tiers.js, #4952).
  const rungs = planOffer({ currentTier, door: 'google', held });

  // Top of the ladder, a deploy whose tier config carries no Play products, or
  // a Play that priced nothing: `withdraw`, so the note stands — or, on a
  // stale repaint, the standing card is dead-ended.
  const buttons = await renderStorePlanCard({
    biller: playBiller,
    $card: $playPurchaseCard,
    $tiers: $playPurchaseTiers,
    $note: $playPurchaseNote,
    setStatus: setPlayPurchaseStatus,
    rungs,
    currentTier,
    // Open on the interval they are already billed on, so a subscriber
    // looking for the rung above theirs is not first asked to change both.
    preferredInterval: fromTier && held ? tierInterval(fromTier) : undefined,
    generation,
    withdraw,
    // What the tap will do, which for a plan change is mostly a question of
    // WHEN (planOffer's `replacement`, and the CTA naming it).
    buy: ({ tier, replacement }, btn) => startPlayPurchase(tier, btn, replacement),
  });
  // What `deadEndPlayLadder` disables on a later stale repaint.
  if (buttons) _playPurchaseButtons = buttons;
  if (generation !== _inAppRenderGeneration) return null;
  if (buttons) return 'priced';
  return rungs.length === 0 ? 'none' : 'unpriced';
}

function setPlayPurchaseStatus(message) {
  if (!$playPurchaseStatus) return;
  $playPurchaseStatus.textContent = message;
  $playPurchaseStatus.hidden = !message;
}

/**
 * Leave the ladder on screen and let nothing on it be tapped (#3910).
 *
 * What a repaint from a stale row does when Play answered nothing it could
 * rebuild from. The rungs standing there were built for the account as the last
 * GOOD read described it, and the read that failed is the read that would have
 * said the customer now holds a Play subscription — so every one of them may be
 * an acquisition offered to a subscriber, which Play opens as a SECOND
 * subscription at its own sheet with the money already taken
 * (docs/constraints/billing-store-google-cards.md § Do not).
 *
 * Neither of the other two answers is available. Rebuilding needs a fact only
 * the failed read or a silent Play could supply, and withdrawing swaps a card
 * for the note — a purchase surface that turns out never to have existed, the
 * one direction docs/constraints/billing-store-cards.md rules out. A dead rung
 * is the third: it takes no money, unsays nothing, and a reload is the way on.
 *
 * The line says so, because a dead button with no copy is the #3901 dead end.
 * No host, no link, no purchase language beyond the plan itself.
 */
function deadEndPlayLadder() {
  for (const btn of _playPurchaseButtons) btn.disabled = true;
  setPlayPurchaseStatus('We could not refresh your plan. Reload the page to change it.');
}

/**
 * The Play subscription this device's Play account already holds, or null.
 *
 * Filtered to the tier catalogue rather than taken as "the first purchase":
 * `listPurchases` returns one-time products too (Play packs, #3452), and a
 * pack's token passed as the thing to replace would be nonsense to Play.
 * `getAllTiers`, not the active set — a grandfathered plan is exactly the one
 * whose holder most needs a route off it.
 */
async function heldPlaySubscription() {
  const subscriptionProducts = new Set(
    getAllTiers().map((t) => t.google?.productId).filter(Boolean),
  );
  const purchases = await listPlayPurchases();
  return purchases.find((p) => subscriptionProducts.has(p.itemId)) ?? null;
}

/**
 * Say where this account's plan lives — the in-shell note, from one place
 * (ADR #3812).
 *
 * Two questions decide this corner of the screen, and they are separate. What
 * the note SAYS comes from the billing record and nothing else: the provider
 * on the row is where the plan lives, whatever any store answered a moment
 * ago. Whether it is SEEN is the store's half: a purchase card covers it when
 * one paints (`hidePlanLocationNote`), because the app's own purchase
 * mechanism replaces the note rather than sitting beside it.
 *
 * Called by every path that ends inside a shell without a card up, so the
 * pairing invariant holds by construction: a subscriber is never left with
 * neither a card nor a note, and never with both. The caller raises it while
 * the card is still hidden — a cold render, always — and every arm that
 * withdraws the card raises it again. The hole that made this one function
 * was the reverse: the caller hid the note up front and a single suppression
 * arm brought it back, so every other arm left the section blank.
 *
 * A FREE account is told nothing. The note answers "where does my
 * subscription live", so an account without one has no location to state:
 * saying a plan is "managed on the web" would name a plan that does not
 * exist, manage nothing, and point at the one destination the no-steering
 * rule will not let it name (docs/constraints/billing-store-cards.md). The
 * allowance card above already says FREE.
 *
 * Naming a store is account management rather than purchase steering — no
 * host, no link, no purchase language.
 */
function showPlanLocationNote(note) {
  if (!$mobileSubscriptionNote || !$mobileSubscriptionText) return;
  if ($mobileSubscriptionLink) $mobileSubscriptionLink.hidden = true;
  if (!note) {
    $mobileSubscriptionNote.hidden = true;
    return;
  }
  // billingView words it by surface: inside a shell where the plan lives, by
  // provider alone; in a browser why a store subscriber has no tier cards.
  // /settings' notes carry no link — in a browser the manage row already
  // routes to the store's page.
  $mobileSubscriptionText.textContent = note.parts.filter((p) => typeof p === 'string').join('');
  $mobileSubscriptionNote.hidden = false;
}

/** Cover the note — called by a purchase card, and only once it has painted. */
function hidePlanLocationNote() {
  if ($mobileSubscriptionNote) $mobileSubscriptionNote.hidden = true;
}

// Run one Play purchase and hand the purchase token to the server. The server
// is what grants the tier — the client never decides it — and it re-reads
// every fact about the purchase from Play, so the token is the only thing that
// has to survive this call intact.
//
// `replacement` makes it a plan change instead of an acquisition (#3045): the
// old purchase token rides in the PaymentRequest, so Play swaps the plan on
// the subscription the customer has rather than starting a second one.
async function startPlayPurchase(tier, btn, replacement = null) {
  btn.disabled = true;
  const sub = await storePayingAccount(btn, setPlayPurchaseStatus);
  if (!sub) return;
  setPlayPurchaseStatus('Contacting Google Play…');

  const result = await purchasePlayProduct(tier.google.productId, replacement, sub);

  if (!result.ok) {
    btn.disabled = false;
    // A cancel is the commonest non-success outcome and is not a failure —
    // showing it as one reads as a bug in the app.
    if (result.reason === 'cancelled') return setPlayPurchaseStatus('');
    return setPlayPurchaseStatus(
      "Couldn't complete the purchase. Please try again.",
    );
  }

  setPlayPurchaseStatus('Confirming your subscription…');
  // The plan before the tap, so a pending payment's wait can tell it moved.
  const tierBefore = _lastAccountData?.tier;
  try {
    const body = await confirmPlayPurchase(result.purchaseToken);
    // A deferred change moves nothing today, so `body.tier` is still the plan
    // they are on — announcing it as "You're on Standard" would report the
    // opposite of what happened. The pending-change notice on the card carries
    // the date once the account reloads below.
    showToast(replacement?.replacementMode === PLAY_REPLACEMENT_DEFERRED
      ? `Your plan changes to ${tierDisplayName(tier.id)} at the end of this period.`
      : `You're on ${tierDisplayName(body.tier)}.`);
    setPlayPurchaseStatus('');
    await fetchAccountInfo();
  } catch (err) {
    btn.disabled = false;
    if (playPurchasePending(err)) return await awaitPlayPlan(tierBefore);
    // The door's older read lost to a newer writer. On a first purchase that
    // is usually Play's own notification granting this very plan (ADR #5125),
    // so the account is re-read before anything says to try again.
    if (err?.status === 409 && errorCodeOf(err) === 'subscription_changed'
      && await playPlanMoved(tierBefore)) return;
    // Play's record names no account: an app version from before #5122. The
    // door refuses it for good and Play refunds it.
    if (errorCodeOf(err) === 'purchase_unattributed') {
      return setPlayPurchaseStatus(PLAY_UNATTRIBUTED_COPY);
    }
    // 409 — three codes, three different facts (ADR #864's code/error split):
    // this purchase belongs to another podstow account, the account is billed
    // by another provider, or the account already holds a different Play
    // subscription (#3695). Google owns refunds and this credential
    // deliberately cannot issue one (#3042), so name the route that can rather
    // than showing a generic failure. Rendered verbatim, so it says what
    // happened and points nowhere else (no purchase host, no /billing).
    //
    // The code rides `err.body` — apiRequest attaches the raw response TEXT
    // and nothing else (clients/shared/api.js), so reading `err.code` would be
    // reading a field nothing ever sets. Keyed on the code, never the status
    // alone: the last time a 409 was read as one case, the same-provider
    // conflict told the customer their other subscription was billed elsewhere.
    const copy = err?.status === 409 ? PLAY_409_COPY.get(errorCodeOf(err)) : undefined;
    if (copy) return setPlayPurchaseStatus(copy);
    // The purchase itself succeeded and the server could not finish with it.
    // Google auto-refunds an unacknowledged purchase after three days, so this
    // is worth retrying rather than leaving.
    setPlayPurchaseStatus(
      'Your purchase went through, but we could not confirm it. Refresh to try again.',
    );
  }
}

// What the Play purchase card says for each 409 `/api/google-purchase`
// answers. A code the client does not know falls through to the retry copy in
// `startPlayPurchase` rather than to any of these: each names a refund or a
// sign-in, and sending someone there for a conflict this does not describe is
// the #3695 bug again.
const PLAY_409_COPY = new Map([
  ['purchase_claimed_elsewhere',
    'This Google Play purchase is already linked to a different podstow account. '
    + 'Sign in to that account, or request a refund in the Play Store under '
    + 'Payments & subscriptions.'],
  // Play took the money for a second subscription this account cannot hold,
  // so the refund route is right; the explanation has to match it.
  ['already_subscribed',
    'This account already has a Google Play subscription, so this purchase did not '
    + 'become your plan. To undo the Google Play charge, request a refund in the Play '
    + 'Store under Payments & subscriptions.'],
  ['already_subscribed_elsewhere',
    'This account already has a subscription billed elsewhere, so it was not charged '
    + 'twice here. To undo the Google Play charge, request a refund in the Play Store '
    + 'under Payments & subscriptions.'],
]);

// The machine-readable `code` an API error carries (ADR #864's code/error
// split). apiRequest throws an Error carrying `status` and `body` — the raw
// response text — so the code has to be parsed back out of the body; there is
// no `err.code`.
function errorCodeOf(err) {
  try {
    return JSON.parse(err?.body ?? '')?.code;
  } catch {
    return undefined;
  }
}

// Hand the purchase token to the server, which is what actually grants the
// tier. Its own function so the flow above and any later replay path reach the
// server the same way.
async function confirmPlayPurchase(purchaseToken, productId) {
  const token = await sessionToken();
  // The same rule as the Apple confirm above, for the same reason: posting a
  // `Bearer null` asks a question the server answers without looking at the
  // purchase. The server credits a Play pack from Play's notification either
  // way, so the cost is only the copy — but a failure the page cannot place
  // would tell a customer Play "could not complete this purchase" when the
  // articles are in fact on their way.
  if (!token) throw new Error('Could not confirm your session. Please try again.');
  const res = await apiRequest('/api/google-purchase', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    // `productId` only for a pack (#3452), and only as a ROUTING key: it
    // chooses which door the server takes, and the server re-reads every fact
    // — including whether that token actually bought that product — from
    // Play. A subscription posts none, because after a plan change the token
    // resolves to whatever the customer is on now and Play's answer is the
    // only truthful one.
    body: JSON.stringify(productId ? { purchaseToken, productId } : { purchaseToken }),
  });
  return res.json();
}

// ---------------------------------------------------------------------------
// Article packs through Play Billing (#3452, ADR #3270)
// ---------------------------------------------------------------------------
// The TWA's own pack door, rendered into the SAME `#pack-card` section the web
// and StoreKit cards use — one section, one heading, one note — with card
// bodies built from DOM nodes carrying Play's own price, because Play reprices
// per region and the configured amount is catalogue input rather than a
// display value (docs/constraints/billing-store.md FF-2). The card itself is
// `renderStorePackCard` over `playBiller` (#4954).
//
// One thing is simpler here than on the subscription card beside it. A Play
// SUBSCRIPTION is bought through `offerDetails.get(0)`, which is why every one
// we sell carries exactly one base plan and no offers (ADR #3453); a one-time
// product resolves through `getOneTimePurchaseOfferDetails()`, which has no
// list to pick the wrong element of. The catalogue's own constraint moves to
// the single `legacyCompatible` purchase option the sync writes, and it is
// asserted at deploy rather than defended here.
//
// Resolves to the card's outcome, as `renderPlayPurchaseCard` does.
async function renderPlayPackCard(currentTier) {
  const painted = await renderStorePackCard({
    biller: playBiller,
    billerName: 'Google Play',
    $planCard: $playPurchaseCard,
    currentTier,
    buy: startPlayPackPurchase,
  });
  return painted?.outcome ?? null;
}

// Buy one pack through Play and hand the purchase token to the server, which
// credits the articles and consumes the purchase (ADR #5124). The page never
// consumes: the account is on Play's own record, so Play's notification
// credits and consumes a pack this page never reports, and Play refunds one
// that nothing credited.
async function startPlayPackPurchase(pack, btn) {
  btn.disabled = true;
  const sub = await storePayingAccount(btn, setPackCardStatus);
  if (!sub) return;
  setPackCardStatus('Contacting Google Play…');
  // What the meter showed before the tap, so the wait below can tell a credit.
  const overageBefore = deriveUsage(_lastAccountData).overage;

  const result = await purchasePlayProduct(packGoogleProductId(pack), null, sub);

  if (!result.ok) {
    // Play refuses to sell a one-time product it still holds — "You already
    // own this item", in its own dialog — and the refusal reaches this page
    // looking exactly like the user's own cancel. It holds one from the
    // purchase until the server consumes it, which for a slow payment is when
    // Play settles. So a held pack of this sku means the refusal was Play's.
    // The button stays dead across the read.
    const sku = packGoogleProductId(pack);
    const held = result.reason === 'cancelled'
      && (await listPlayPurchases()).some((p) => p.itemId === sku);
    btn.disabled = false;
    if (held) return setPackCardStatus(PLAY_PACK_STILL_FINISHING);
    if (result.reason === 'cancelled') return setPackCardStatus('');
    return setPackCardStatus("Couldn't complete the purchase. Please try again.");
  }

  setPackCardStatus('Adding your articles…');
  try {
    await confirmPlayPurchase(result.purchaseToken, packGoogleProductId(pack));
  } catch (err) {
    btn.disabled = false;
    if (playPurchasePending(err)) return await awaitPlayPackCredit(pack, overageBefore);
    // 403 — the pack is not on offer to this tier. The card filter should have
    // kept it off screen, so reaching here means the withdrawal landed while
    // this page was open. Nothing credited it, so nothing consumes it, and
    // Google refunds the charge itself within three days.
    if (err?.status === 403) {
      return setPackCardStatus(
        'This article pack isn\'t available on your plan, so nothing was added. '
        + 'Google Play refunds a purchase we don\'t deliver.',
      );
    }
    if (packRefusedForGood(err)) {
      return setPackCardStatus(
        'Google Play could not complete this purchase here. '
        + 'If you were charged, the charge is refunded automatically.',
      );
    }
    // The purchase itself succeeded, and Play's notification reaches the
    // server whatever happened to this post. Don't imply it was lost.
    setPackCardStatus(
      'Your purchase went through. Your articles are added as soon as Google Play confirms it.',
    );
    return;
  }
  // A toast rather than the status line: `fetchAccountInfo` re-renders the
  // pack card, and that render clears the line. The re-render re-enables the
  // button, on a read that fails as well as one that works (#3901).
  showToast(`${packCardHeading(pack)} added to your balance.`);
  setPackCardStatus('');
  await fetchAccountInfo();
}

// A slow payment method — Play's "Slow test card", or a real method that
// settles minutes or days later. Play hands back a token before it has the
// money, and the door answers 409 `purchase_pending` (src/billing/google/purchase.ts).
// Play's settlement notification then grants on the server — credits and
// consumes a pack (ADR #5124), acquires a subscription (ADR #5125) — with this
// page open or not, so the page only has to notice: it re-reads `/api/me`
// until the balance grows or the plan moves, for about 13½ minutes. The slow
// test card settles in about four. Past that the card says the purchase lands
// once Play has the money, which is true with the app closed.
const PLAY_PENDING_DELAYS_MS = [10_000, 20_000, 30_000, 30_000, ...Array(12).fill(60_000)];
let playPendingDelaysMs = PLAY_PENDING_DELAYS_MS;

const PLAY_PACK_PENDING_NOW = 'Google Play is still processing your payment. '
  + 'Your articles will be added as soon as it goes through.';
const PLAY_PACK_PENDING_LATER = 'Google Play is still processing your payment. '
  + 'Your articles are added when it goes through, even if the app is closed.';
const PLAY_PLAN_PENDING_NOW = 'Google Play is still processing your payment. '
  + 'Your plan starts as soon as it goes through.';
const PLAY_PLAN_PENDING_LATER = 'Google Play is still processing your payment. '
  + 'Your plan starts when it goes through, even if the app is closed.';
const PLAY_PACK_STILL_FINISHING = 'Google Play is still finishing an earlier purchase '
  + 'of this pack. Please try again in a moment.';

function playPurchasePending(err) {
  return err?.status === 409 && errorCodeOf(err) === 'purchase_pending';
}

const PLAY_UNATTRIBUTED_COPY = 'Google Play did not link this purchase to your account, '
  + 'so it did not become your plan. Update the app and try again. Google Play refunds '
  + 'the charge automatically.';

// Re-read the account; true, with the plan announced, where it has moved off
// `tierBefore`.
async function playPlanMoved(tierBefore) {
  await fetchAccountInfo();
  const tier = _lastAccountData?.tier;
  if (!tier || tier === tierBefore) return false;
  showToast(`You're on ${tierDisplayName(tier)}.`);
  setPlayPurchaseStatus('');
  return true;
}

// A pending payment's wait: re-read until `landed` says the grant is in, or
// the delays run out. Each read re-renders the card, which clears its status
// line, so the line is set again after every read that found nothing.
async function awaitPlayGrant({ setStatus, now, later, landed }) {
  setStatus(now);
  for (const delayMs of playPendingDelaysMs) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    if (await landed()) return;
    setStatus(now);
  }
  setStatus(later);
}

function awaitPlayPlan(tierBefore) {
  return awaitPlayGrant({
    setStatus: setPlayPurchaseStatus,
    now: PLAY_PLAN_PENDING_NOW,
    later: PLAY_PLAN_PENDING_LATER,
    landed: () => playPlanMoved(tierBefore),
  });
}

function awaitPlayPackCredit(pack, overageBefore) {
  return awaitPlayGrant({
    setStatus: setPackCardStatus,
    now: PLAY_PACK_PENDING_NOW,
    later: PLAY_PACK_PENDING_LATER,
    landed: async () => {
      await fetchAccountInfo();
      if (deriveUsage(_lastAccountData).overage <= overageBefore) return false;
      showToast(`${packCardHeading(pack)} added to your balance.`);
      setPackCardStatus('');
      return true;
    },
  });
}

// ---------------------------------------------------------------------------
// Article packs through StoreKit (#3327, ADR #3270)
// ---------------------------------------------------------------------------
// The shell's own pack door. Rendered into the SAME `#pack-card` section the
// web cards use — one section, one heading, one note — but with card bodies
// built from DOM nodes carrying StoreKit's `displayPrice`, because Apple
// reprices per territory and the configured amount is catalogue input rather
// than a display value (docs/constraints/billing-store.md).
//
// The card itself is `renderStorePackCard` over `appleBiller` (#4954); the
// sibling it is ordered against is the StoreKit plan card, not the Stripe one.
async function renderApplePackCard(currentTier) {
  const painted = await renderStorePackCard({
    biller: appleBiller,
    billerName: 'the App Store',
    $planCard: $applePurchaseCard,
    currentTier,
    buy: startApplePackPurchase,
  });
  if (painted?.buttons) {
    _appleCardButtons.pack = painted.buttons;
    holdAppleCardsForTimeout();
  }
}

/**
 * One store pack card, over either biller (#4954): the Apple and the Play pack
 * cards are this renderer with a different `biller` (`appleBiller`,
 * `playBiller`), the name the trust line gives it, the plan card it is ordered
 * against and the purchase a tap starts.
 *
 * Deliberately NOT renderPackCards() from shared/packs.js, for the reason the
 * plan card gives: that helper interpolates into innerHTML, and every string
 * here comes from the store rather than our own config. The words, the
 * heading, the feature lines, the eligibility filter and the card ORDER still
 * come from shared/packs.js, so a shell cannot describe or rank a pack
 * differently from the web.
 *
 * Nothing here may reference a Stripe price id, /billing, or the web purchase
 * host: this is the app's own purchase mechanism, and the moment it pointed
 * anywhere else it would be the external-steering call to action Guideline
 * 3.1.1 and Play's payments policy forbid. Pinned by test/spa-settings.test.ts.
 *
 * Never gated on `hasPackDoor()`: this card is what fills that latch
 * (docs/constraints/billing-store-cards.md).
 *
 * Resolves to `{ outcome, buttons }` — 'priced' with the buttons it painted,
 * 'unpriced' where the store priced none of the packs, 'none' where there was
 * nothing to ask it about — or null where a later render took over.
 */
async function renderStorePackCard({ biller, billerName, $planCard, currentTier, buy }) {
  if (!$packCard || !$packTiers) return { outcome: 'none' };
  const generation = _inAppRenderGeneration;

  // Eligibility is the config lever (ADR #3270), read live. The server refuses
  // an ineligible purchase regardless — this filter is a rendering rule.
  const packs = packsForTier(currentTier).filter((p) => biller.productIdOf(p));
  const ids = packs.map((p) => biller.productIdOf(p));
  // Taken before the no-packs return: only the first render may read the
  // warm-up's answer, and every later one asks the store itself.
  const warmed = takeWarmedPackProducts(biller.store, ids);
  if (packs.length === 0) {
    // Every pack withdrawn from this tier, PODSTOW_PACKS not injected on this
    // deployment, or a pack config that predates the store products. Nothing
    // to sell, and nothing to say about it — the trust line included, which is
    // a SIBLING of the card rather than a child of it and would otherwise be
    // left naming a biller with no cards under it (#3452).
    $packCard.hidden = true;
    if ($packCardTrust) $packCardTrust.hidden = true;
    return { outcome: 'none' };
  }

  // Nothing is painted before the store answers, the section's own visibility
  // included (ADR #3812): the displayed price has to be the store's, and
  // whether there is a section at all depends on the same answer. Both
  // billers' `load` resolve rather than hang, so the wait is bounded. A
  // warm-up that priced nothing before this render is not handed over
  // (#4206), so this asks the store itself.
  const products = await (warmed ?? biller.load(ids));
  if (generation !== _inAppRenderGeneration) return null;

  // Nothing priced is nothing to sell, and the trust line goes with the cards
  // rather than being left to name a biller with nothing under it (#3452).
  // A partial catalogue still renders: the packs the store priced are a true
  // offer, and the rest say "Unavailable" below.
  if (!biller.anyPriced(products, ids)) {
    $packCard.hidden = true;
    if ($packCardTrust) $packCardTrust.hidden = true;
    return { outcome: 'unpriced' };
  }

  $packCard.hidden = false;
  // The door is proven open, so the wall may name it (#3416): one answer for
  // the card and the refusal copy, never two.
  notePackPriced();
  setPackCardStatus('');
  if ($packCardNote) $packCardNote.textContent = packCardNote(currentTier);
  // Who takes the money, said plainly — the plan card's trust line, for the
  // other product. No price, no host, no link.
  showPackBiller(billerName);
  // A non-paid tier leads with the subscription, a paid or lapsed one with the
  // pack (PRD #534, ADR #3591). The sibling is this store's plan card.
  applyPurchaseOrder($packCard, $planCard, currentTier);

  $packTiers.innerHTML = '';
  const buttons = [];
  for (const pack of packs) {
    const displayPrice = biller.priceOf(products[biller.productIdOf(pack)]);
    const card = document.createElement('div');
    card.className = 'tier';
    card.setAttribute('data-pack', pack.id);

    const heading = document.createElement('h3');
    heading.textContent = packCardHeading(pack);
    card.appendChild(heading);

    const price = document.createElement('div');
    price.className = 'price';
    if (displayPrice) {
      price.textContent = displayPrice;
      const kind = document.createElement('span');
      // "one-off" where a plan card carries its interval: the single most
      // important thing to establish before the button is that this is not a
      // subscription.
      kind.textContent = ' one-off';
      price.appendChild(kind);
    } else {
      // The store could not price the product — a catalogue that has not
      // propagated, a purchase option still DRAFT, a build the store does not
      // sell to. Say so rather than inventing a number.
      price.textContent = 'Unavailable';
    }
    card.appendChild(price);

    const features = document.createElement('ul');
    for (const line of packCardLines(pack)) {
      const li = document.createElement('li');
      li.textContent = line;
      features.appendChild(li);
    }
    card.appendChild(features);

    const btn = document.createElement('button');
    btn.className = 'btn btn-outline';
    btn.textContent = PACK_CTA_LABEL;
    // Which pack this buys, in the name: #3930 dumped this very screen and
    // found three buttons all called `Buy pack`
    // (docs/constraints/accessible-names.md).
    btn.setAttribute('aria-label', `${PACK_CTA_LABEL}, ${packCardHeading(pack)}`);
    btn.disabled = !displayPrice;
    btn.addEventListener('click', () => runPurchase(() => buy(pack, btn)));
    buttons.push(btn);
    card.appendChild(btn);

    $packTiers.appendChild(card);
  }
  return { outcome: 'priced', buttons };
}

// Whether a failed confirm means "never try this again".
//
// The unfinished consumable is a RETRY, and a retry is only worth having when
// the failure could go the other way next time. A 5xx or a dropped connection
// is exactly that. A 4xx is not: the server has looked at this transaction and
// refused it — the pack is withdrawn from the tier, the transaction is revoked,
// its account token names someone else — and the answer will not change on the
// next launch. Leaving one of those unfinished re-posts it forever, on every
// launch, against a rate-limited endpoint, and shows the user the same failure
// each time.
//
// Two 4xx are the exceptions, and both say the server never judged the
// transaction:
//
// - 429 means "not now", which is the one thing a retry does fix.
// - 401 means the credential we presented was not accepted — a revoked
//   session, a scope change, a token expiring between the read and the post.
//   The transaction is untouched by that verdict and the next launch may well
//   be authenticated, so finishing on one throws away a pack the customer has
//   paid for (#3924). The door's remaining 4xx are all judgments of the
//   transaction itself: 400 invalid or revoked, 403 not on offer to this tier
//   or not this account's, 409 the token already bound elsewhere.
//
// Finishing costs nothing that is not already lost. `ONE_TIME_CHARGE` is the
// authoritative fulfillment (docs/constraints/billing-packs-store.md) and credits a
// purchase the server should have taken regardless of whether the client ever
// finished its transaction.
function packRefusedForGood(err) {
  const status = err?.status;
  if (status === 401 || status === 429) return false;
  return typeof status === 'number' && status >= 400 && status < 500;
}

// Buy one pack through StoreKit and hand the signed transaction to the server,
// which is what actually credits the articles.
//
// The finish is the load-bearing step. A consumable stays unfinished until the
// server says the credit landed, and StoreKit re-delivers an unfinished one on
// every launch — so a confirm that fails costs the user nothing but a wait,
// while finishing early would throw away a purchase they paid for. That is why
// `finishAppleTransaction` is reached only from the success path, and why the
// failure branch says the purchase went through rather than that it was lost.
async function startApplePackPurchase(pack, btn) {
  btn.disabled = true;
  // A pre-flight that fails gives the button back, unless a timed-out purchase
  // still holds it; then the timeout's line, not "try again", is what stands.
  const sub = await storePayingAccount(btn, setPackCardStatus, releaseAppleButton);
  if (!sub) return holdAppleCardsForTimeout();
  setPackCardStatus('Contacting the App Store…');

  const result = await purchaseAppleProduct(packAppleProductId(pack), sub);

  if (!result.ok) {
    // A timeout takes it back with the rest of the Apple buttons (#5281).
    releaseAppleButton(btn);
    if (result.reason === 'timeout') {
      return holdAppleCardsUntilAnswered(result, setPackCardStatus, 'Your articles arrive');
    }
    return setPackCardStatus(appleNotOkLine(result, 'Your articles arrive'));
  }

  setPackCardStatus('Adding your articles…');
  try {
    await confirmAppleTransaction(result.signedTransaction);
    await finishAppleTransaction(result.transactionId);
    showToast(`${packCardHeading(pack)} added to your balance.`);
    setPackCardStatus('');
    await fetchAccountInfo();
  } catch (err) {
    releaseAppleButton(btn);
    // A refusal the next attempt would repeat: stop StoreKit re-delivering it.
    if (packRefusedForGood(err)) await finishAppleTransaction(result.transactionId);
    // 403 — the pack is not on offer to this tier. The card filter should have
    // kept it off screen, so reaching here means the withdrawal landed while
    // this page was open. Apple has taken the money and only Apple can give it
    // back, so name the route that can.
    if (err?.status === 403) {
      return setPackCardStatus(
        'This article pack isn\'t available on your plan, so nothing was added. '
        + 'To undo the App Store charge, request a refund at reportaproblem.apple.com.',
      );
    }
    if (packRefusedForGood(err)) {
      return setPackCardStatus(
        'The App Store could not complete this purchase here. If you were charged, '
        + 'request a refund at reportaproblem.apple.com.',
      );
    }
    // Transient: the transaction stays unfinished, so it is retried on the next
    // launch — and Apple\'s own notification credits it anyway.
    setPackCardStatus(
      'Your purchase went through. Your articles can take a moment to appear — '
      + 'refresh if they do not.',
    );
  }
}

// Inline plan picker — sits next to the subscription card so users can see
// their options without a hop to /billing. Rendered only where billingView's
// plan door is the web's: never inside a native app shell (App Store / Play
// Store ToS forbid in-app entry points to web payments — see isNativeWebview),
// and never for a store subscriber (their plan changes go through the store;
// the Stripe checkout would 409 — ADR #2867 §1).
//
// A 'portal' door is the one-door split (#3024, ADR #3028): a live Stripe
// subscription changes plan at Stripe, so its cards deep-link at the portal
// and the whole paid ladder becomes reachable. Only an account with nothing
// live routes at checkout.
function renderUpgradeCard(currentTier, view) {
  if (!$upgradeCard || !$upgradeTiers) return;
  if (view.planDoor !== 'checkout' && view.planDoor !== 'portal') {
    $upgradeCard.hidden = true;
    return;
  }
  const portalRouted = view.planDoor === 'portal';
  // The intro is an upsell for an account with nothing live, and the wrong
  // thing above a picker offering a subscriber a CHEAPER plan. What they need
  // there is when a change takes effect — ADR #3181's decision, stated
  // nowhere else on the page.
  //
  // Written BOTH ways, not just on the way in: this render re-runs within one
  // page load (late TWA-port arrival, the return from Apple's manage sheet,
  // every /me refresh), so a one-way swap would leave an account that churned
  // mid-session reading plan-change timing above a checkout picker. The upsell
  // copy lives in the markup, so it is remembered the first time we see it
  // rather than duplicated here. A grandfathered plan adds its one-way
  // warning (docs/constraints/billing-stripe.md) — `planChangeNote` keys that
  // on the row's flags, never a tier id.
  if ($upgradeCardIntro) {
    _upgradeCardUpsell ??= $upgradeCardIntro.textContent;
    $upgradeCardIntro.textContent = view.planChangeNote ?? _upgradeCardUpsell;
  }

  // Only /billing can mint a portal session, so both portal CTAs hand off to
  // it through the shared deep-link param and it opens one on arrival.
  const openPortal = () => {
    window.location.href = `/billing/?${PORTAL_DEEP_LINK_PARAM}=1`;
  };
  const rendered = renderTierCardsShared({
    container: $upgradeTiers,
    currentTier,
    portalRouted,
    cancelled: view.cancelled,
    requirePriceId: false,
    onUpgrade: (tier) => {
      // The ask is recorded here rather than where the checkout session is
      // requested — see the upgrade-funnel block in shared/analytics.js.
      emitUpgradeIntent({
        surface: UPGRADE_SURFACE_SETTINGS,
        tier: currentTier,
        targetTier: tier.id,
      });
      window.location.href = `/billing/?upgrade=${encodeURIComponent(tier.id)}`;
    },
    // A plan change on a live subscription happens at Stripe, up the ladder
    // or down it. No upgrade-intent emit: the funnel measures acquisition,
    // and its bottom (`stripe-checkout-completed`) is unreachable from here.
    onChangePlan: openPortal,
    // Grace window only: un-cancelling lives in the Stripe portal (a fresh
    // checkout would open a second subscription). Not an upgrade ask either.
    onResubscribe: openPortal,
  });
  // 0 means there is nothing left to offer — a subscriber on the only paid
  // tier there is, or PODSTOW_TIERS wasn't injected on this deployment.
  // Either way, leave the card hidden.
  $upgradeCard.hidden = rendered === 0;
  // Outlives this call: the nav path reports views too (see showSection), and
  // a portal-routed picker is outside the upgrade funnel entirely.
  _upgradeCardsPortalRouted = !view.funnelTop;
  maybeEmitPricingView(currentTier);
}

// Article packs (#3272). The web door only: inside a store shell the pack is
// the platform biller's consumable (#3327) and a Stripe card here would be
// exactly the purchase-host copy Guideline 3.1.1 forbids
// (docs/constraints/billing-store.md).
//
// The card names no price id: /settings carries the pack catalogue but not the
// price map, so the CTA hands off to /billing by pack id — the same shape the
// tier cards use for `?upgrade=`, and the reason `requirePriceId` is false.
function renderPackCard(currentTier, suppress) {
  if (!$packCard || !$packTiers) return;
  if (suppress) {
    $packCard.hidden = true;
    renderAutoTopUpOptIn(0);
    return;
  }
  const rendered = renderPackCardsShared({
    container: $packTiers,
    currentTier,
    requirePriceId: false,
    onBuy: (pack) => {
      // The opt-in rides the link (#3273). It has to: /billing takes this
      // param straight into Stripe's sheet, so its own cards — and the
      // checkbox on them — never paint for anyone who arrived this way.
      const auto = $packAutoOptIn?.checked === true ? '&auto=1' : '';
      window.location.href = `/billing/?pack=${encodeURIComponent(pack.id)}${auto}`;
    },
  });
  // 0 means no pack is on offer to this tier — the ADR #3270 withdrawal lever
  // pulled, or PODSTOW_PACKS wasn't injected on this deployment.
  $packCard.hidden = rendered === 0;
  renderAutoTopUpOptIn(rendered);
  if (rendered === 0) return;
  if ($packCardNote) $packCardNote.textContent = packCardNote(currentTier);
  // A non-paid tier leads with the subscription, a paid or lapsed one with the
  // pack (PRD #534, ADR #3591). One rule, in shared/packs.js, so /billing
  // cannot order them differently.
  applyPurchaseOrder($packCard, $upgradeCard, currentTier);
}

/**
 * Automatic top-up's opt-in, on the pack card (#3273).
 *
 * A checkbox against the purchase the cards are about to make, which rides the
 * /billing deep link as `auto=1` — the webhook arms the setting when that
 * checkout settles, so there is nothing to save here and nothing half-done to
 * leave behind. Offered only while nothing is armed: changing the pack or the
 * card is another purchase with the box ticked
 * (docs/constraints/billing-packs-auto-topup.md).
 *
 * No `isNativeWebview()` of its own, deliberately. A store shell never reaches
 * this function with anything to show: `renderInAppDependentParts` calls it
 * with 0 for any shell render, before it sends `inApp` to the store renderers
 * — which paint into this same `#pack-card` — or suppresses the card, which
 * calls it with 0 too (docs/constraints/billing-store-cards.md).
 *
 * `packsRendered` rather than the tier, because it is the same answer the
 * cards reached: a tier every pack is withdrawn from has nothing to arm, and
 * offering it would be a dead end (#3416's shape).
 */
function renderAutoTopUpOptIn(packsRendered) {
  // Read from the module's own holder for the same reason the in-app parts
  // are: the pack card is re-rendered by paths that carry no /me response
  // (the Play capability probe resolving, a shell marker arriving late).
  const offer = packsRendered > 0 && (_lastAccountData?.autoTopUpPackId ?? null) === null;
  if ($packAutoOptInRow) $packAutoOptInRow.hidden = !offer;
  if (!offer && $packAutoOptIn) $packAutoOptIn.checked = false;
}

/**
 * Automatic top-up once armed, and what it has bought (#3273).
 *
 * Its own card rather than a part of `#pack-card`, because both halves must
 * outlive that card (#4126). Armed, it says what it will do and offers the one
 * control that exists afterwards, OFF — even on a tier every pack is withdrawn
 * from, where the pack card is gone but the setting is still on the row and
 * would charge again the day the lever is released. Off, it keeps the log of
 * past charges, because that is when a user checking one comes looking.
 *
 * Outside `#pack-card`, it does not inherit that card's containment, so it
 * takes the shell answer itself, and a shell render hands off before anything
 * names a pack, a price or the log (docs/constraints/billing-packs-auto-topup.md).
 */
function renderAutoTopUpCard(data, inApp) {
  if (!$autoTopUpCard) return;
  if (inApp) {
    renderShellAutoTopUpCard(data);
    return;
  }
  const armedPack = data?.autoTopUpPackId ?? null;
  const history = Array.isArray(data?.autoTopUpHistory) ? data.autoTopUpHistory : [];
  $autoTopUpCard.hidden = armedPack === null && history.length === 0;
  if ($autoTopUpCard.hidden) return;

  if ($autoTopUpOff) $autoTopUpOff.hidden = armedPack === null;
  if ($autoTopUpState) {
    const pack = armedPack === null ? null : getAllPacks().find((p) => p.id === armedPack);
    $autoTopUpState.textContent = armedPack === null
      ? 'Automatic top-up is off.'
      : pack
        ? `Automatic top-up is on: we\u2019ll buy ${packCardHeading(pack)} whenever you run out, `
          + 'and email you a receipt each time.'
        // Armed with a pack this deploy no longer sells. Say the true half —
        // it is on, and here is how to stop it — rather than a size that is
        // no longer on the shelf.
        : 'Automatic top-up is on. We\u2019ll buy a pack whenever you run out.';
  }
  renderAutoTopUpLog(history);
}

/**
 * The same card inside a store shell (#4556, ADR #4555): a shell may manage
 * what it cannot sell. A save here can fire the charge, so an armed account
 * sees that it is on and OFF, and nothing else — no pack or price (the line
 * names none), no log (it names amounts), no opt-in (it arms a purchase). An
 * unarmed account sees no card: there is nothing here to stop.
 *
 * The log is emptied and the line reset, hidden card or not, because a web
 * render of this page may have painted both before a late shell marker arrived.
 */
function renderShellAutoTopUpCard(data) {
  const armed = (data?.autoTopUpPackId ?? null) !== null;
  $autoTopUpCard.hidden = !armed;
  if ($autoTopUpLog) {
    $autoTopUpLog.innerHTML = '';
    $autoTopUpLog.hidden = true;
  }
  if ($autoTopUpOff) $autoTopUpOff.hidden = !armed;
  if ($autoTopUpState) $autoTopUpState.textContent = armed ? 'Automatic top-up is on.' : '';
}

/** `2 Sep` — short, and in the reader's own locale rather than ours. */
function autoTopUpDate(iso) {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function renderAutoTopUpLog(rows) {
  if (!$autoTopUpLog) return;
  $autoTopUpLog.innerHTML = '';
  for (const row of rows) {
    const pack = getAllPacks().find((p) => p.id === row.packId);
    const li = document.createElement('li');
    // The pack and the amount, which is what the constraint asks the line to
    // carry. The price comes from the same helper the cards use, because a
    // second copy is how "$4" and "$4.00" end up on one page.
    li.textContent = [
      autoTopUpDate(row.at),
      pack ? packCardHeading(pack) : row.packId,
      packPriceLabel(row.amountCents),
    ].filter(Boolean).join(' \u2014 ');
    $autoTopUpLog.appendChild(li);
  }
  $autoTopUpLog.hidden = rows.length === 0;
}

/**
 * Turn it off, now.
 *
 * A DELETE and a re-read rather than a local flip: the server owns whether it
 * is on, and the next save reads the row, so the honest thing to show is what
 * the row now says.
 *
 * The repaint hides the button focus was on, so focus is handed on (#4182):
 * to the status line while the log keeps the card up — it is a live region,
 * and says "off" — or, when the card hides with that line in it, to the
 * Subscription section itself, with the confirmation spoken through the toast
 * stack, the live region outside the card.
 */
async function disableAutoTopUp() {
  const token = await sessionToken();
  if (!token) return;
  if ($autoTopUpOff) $autoTopUpOff.disabled = true;
  try {
    await apiRequest('/api/auto-topup', {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchAccountInfo();
    // The card repaints behind this promise (renderSubscriptionCard), and
    // that callback was registered first, so it has run once this resolves.
    await awaitTwaSettled();
  } catch (err) {
    console.error('Failed to turn off automatic top-up:', err);
    showToast('Could not turn off automatic top-up.', 'error');
  } finally {
    if ($autoTopUpOff) $autoTopUpOff.disabled = false;
  }
  handOnAutoTopUpFocus();
}

// Where focus goes once OFF has settled (#4182). Only focus the button lost
// is moved — disabling a focused button blurs it, and so does hiding it — so a
// user who put it elsewhere during the round trips keeps it there.
function handOnAutoTopUpFocus() {
  if (!$autoTopUpCard) return;
  const active = document.activeElement;
  const focusDropped = !active || active === document.body || active === $autoTopUpOff;
  if ($autoTopUpCard.hidden) {
    showToast('Automatic top-up is off.', 'success');
    if (focusDropped) $subscriptionCard?.focus();
  } else if ($autoTopUpOff && !$autoTopUpOff.hidden) {
    // Still armed: the DELETE was refused, or the re-read did not see it.
    if (focusDropped) $autoTopUpOff.focus();
  } else if (focusDropped) {
    $autoTopUpState?.focus();
  }
}

// Report the funnel top (#2914) iff the upgrade cards are genuinely on screen.
// Both halves are read straight off the element, so the signal cannot drift
// from what CSS actually paints:
//   `hidden`         — cleared just above, only when there are tiers to offer.
//   `dataset.active` — set by showSection when Subscription is the section on
//                      screen; style.css display:none's every
//                      [data-settings-section] without it, and a mobile
//                      /settings load with no hash paints the section LIST,
//                      not the card.
// The two settle in either order — a #subscription deep-link activates the
// section before /me returns, a sidebar click activates it long after — so
// both the render and the nav call this and whichever completes the pair wins.
// emitPricingView is once-per-page-load, so the repeat calls a re-render (late
// TWA-port arrival) or a nav round-trip produces are no-ops.
//
// The render path passes the tier it just rendered against; the nav path has
// no tier of its own and falls back to the last one /me reported.
function maybeEmitPricingView(tier) {
  if (!$upgradeCard || $upgradeCard.hidden) return;
  if ($upgradeCard.dataset?.active !== 'true') return;
  // Portal-routed cards are not a funnel top (#3024): a plan change made at
  // Stripe never produces the `stripe-checkout-completed` this funnel ends
  // at, so counting the view would deflate every conversion rate by the
  // volume of subscribers browsing their options.
  if (_upgradeCardsPortalRouted) return;
  emitPricingView({
    surface: UPGRADE_SURFACE_SETTINGS,
    tier: tier ?? _lastAccountData?.tier ?? 'free',
  });
}

// ---------------------------------------------------------------------------
// Connected MCP apps (issue #873) — list authorisations the user has
// granted to AI assistants and let them revoke individual apps.
// ---------------------------------------------------------------------------
async function fetchConnectedApps() {
  const token = await sessionToken();
  if (!token) throw new Error('Not authenticated');
  // apiRequest (not raw fetch) for the client-identity header (ADR #1627)
  // and transient-failure retry; it throws on non-2xx, matching this
  // function's contract.
  const res = await apiRequest('/api/connected-apps', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
}

async function revokeConnectedApp(originJti) {
  const token = await sessionToken();
  if (!token) throw new Error('Not authenticated');
  // Throws on failure — the revoke button's catch shows the error toast.
  await apiRequest(`/api/connected-apps/${encodeURIComponent(originJti)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
}

function scopeLabel(scope) {
  if (scope === 'episodes:create') return 'Save episodes to podstow';
  if (scope === 'episodes:read')   return 'Read your episodes and account state';
  return scope;
}

function relativeTime(iso) {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (seconds < 60)        return 'just now';
  if (seconds < 3600)      return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400)    return `${Math.floor(seconds / 3600)} hr ago`;
  if (seconds < 30 * 86_400) {
    const days = Math.floor(seconds / 86_400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  return new Date(t).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

async function renderConnectedApps() {
  if (!$appsCard || !$appsList || !$appsEmpty) return;

  let data;
  try {
    data = await fetchConnectedApps();
  } catch (err) {
    console.error('Failed to load connected apps:', err);
    $appsCard.dataset.state = 'error';
    return;
  }
  const apps = Array.isArray(data?.apps) ? data.apps : [];

  $appsList.replaceChildren();
  if (apps.length === 0) {
    $appsEmpty.hidden = false;
  } else {
    $appsEmpty.hidden = true;
    for (const app of apps) {
      const li = document.createElement('li');
      li.className = 'app-row';

      const main = document.createElement('div');
      main.className = 'app-main';

      const appLabel = app.clientName || 'Unnamed app';
      const name = document.createElement('div');
      name.className = 'app-name';
      name.textContent = appLabel;
      main.appendChild(name);

      // The verified publisher domain, for an app registered by Client ID
      // Metadata Document (ADR #2988 §7). Its own element rather than text
      // appended to the name: the name is self-asserted and the domain is
      // not, so running them together would let a crafted name imitate one.
      // It also makes two devices of the same app tellable apart, which
      // matters because CIMD grants are deliberately not deduplicated (§6).
      // Absent for a DCR app, which has no domain to show.
      if (app.clientOrigin) {
        const origin = document.createElement('div');
        origin.className = 'app-origin';
        origin.textContent = app.clientOrigin;
        main.appendChild(origin);
      }

      if (Array.isArray(app.scopes) && app.scopes.length > 0) {
        const scopes = document.createElement('ul');
        scopes.className = 'app-scopes';
        for (const s of app.scopes) {
          const item = document.createElement('li');
          item.textContent = scopeLabel(s);
          scopes.appendChild(item);
        }
        main.appendChild(scopes);
      }

      const used = document.createElement('div');
      used.className = 'app-last-used';
      used.textContent = app.lastUsedAt ? `Last used ${relativeTime(app.lastUsedAt)}` : 'Not used yet';
      main.appendChild(used);

      const revokeBtn = document.createElement('button');
      revokeBtn.type = 'button';
      revokeBtn.className = 'btn btn-outline btn-compact app-revoke';
      // One button per authorised app, all reading "Revoke", so the name says
      // WHICH app this one drops — the row's own data, as
      // docs/constraints/accessible-names.md requires of a list. The face and
      // the name move together: a button that says "Revoking…" while still
      // named "Revoke" tells a screen-reader user nothing happened.
      const paintRevoke = (face) => {
        revokeBtn.textContent = face;
        revokeBtn.setAttribute('aria-label', `${face}, ${appLabel}`);
      };
      paintRevoke('Revoke');
      revokeBtn.addEventListener('click', async () => {
        revokeBtn.disabled = true;
        paintRevoke('Revoking…');
        try {
          await revokeConnectedApp(app.originJti);
          showToast(`Revoked ${app.clientName || 'app'}.`);
          await renderConnectedApps();
        } catch (err) {
          console.error('Revoke failed:', err);
          showToast('Revoke failed.', 'error');
          revokeBtn.disabled = false;
          paintRevoke('Revoke');
        }
      });

      li.appendChild(main);
      li.appendChild(revokeBtn);
      $appsList.appendChild(li);
    }
  }
  $appsCard.dataset.state = 'loaded';
}

// ---------------------------------------------------------------------------
// Copy helpers
// ---------------------------------------------------------------------------

async function copyMcpUrl() {
  try {
    await navigator.clipboard.writeText($mcpUrlInput.value);
    flashCopied(document.getElementById('copy-mcp-btn'), { announce: 'MCP server URL copied' });
  } catch {
    showToast('Could not copy.', 'error');
  }
}

// ---------------------------------------------------------------------------
// Submit a suggestion via the Feedback API
// ---------------------------------------------------------------------------
async function submitSuggestion() {
  const $suggestionText = document.getElementById('suggestion-text');
  const $suggestionBtn = document.getElementById('suggestion-btn');
  const text = $suggestionText.value.trim();

  if (!text) {
    showToast('Enter a suggestion.', 'error');
    return;
  }

  $suggestionBtn.disabled = true;
  $suggestionBtn.textContent = 'Submitting…';

  try {
    const token = await sessionToken();
    if (!token) {
      showToast('Sign in to submit a suggestion.', 'error');
      return;
    }
    // apiRequest throws `HTTP <status>` with the body attached; reshape it
    // into the message this catch block has always toasted. `retry: false`:
    // this POST grants an extra-article credit server-side (#1772) and carries
    // no Idempotency-Key, so an ambiguous 5xx/network failure must surface to
    // the user rather than be blindly resubmitted and double-credited.
    const res = await apiRequest('/api/feedback', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        type: 'suggestion',
        text,
      }),
    }, { retry: false }).catch((err) => {
      if (err.status) throw new Error(`Server returned ${err.status}: ${err.body ?? ''}`);
      throw err;
    });

    // Reward genuine suggestions with an extra article when we have trust
    // headroom (#1772); otherwise just thank them (the credit, if the
    // suggestion is acted on, lands at triage). "credit … balance" matches the
    // standing note on the form and the problem-report copy — never
    // "allowance", which is the per-period base the credit sits on top of.
    const data = await res.json().catch(() => ({}));
    showToast(data.credited
      ? "Suggestion submitted — thanks! We've added a credit back to your balance."
      : 'Suggestion submitted — thank you!');
    $suggestionText.value = '';
  } catch (err) {
    console.error(err);
    showToast(err.message ?? 'Failed to submit suggestion', 'error');
  } finally {
    $suggestionBtn.disabled = false;
    $suggestionBtn.textContent = 'Submit suggestion';
  }
}

// ---------------------------------------------------------------------------
// Voices (ticket #736)
// ---------------------------------------------------------------------------
let voiceSettings = null;
let selectedVoiceLang = null;

// Per-session record of which region concertinas the user has opened.
// Regions default to closed on first render; re-renders triggered by
// toggling a voice / "all" / "none" preserve the user's open/closed
// state instead of slamming everything shut. Keyed by `${lang}/${region}`
// so switching language doesn't leak open state across catalogues.
const voiceRegionOpen = new Map();
function regionOpenKey(region) {
  return `${selectedVoiceLang}/${region}`;
}

// Region quick-select (ticket #747). BCP 47 locale → region subtag.
// "en-US" → "US"; "arb" (MSA, no region) → null. The voice catalogue uses
// simple `lang-REGION` locales, so the second segment is always the region.
function extractRegion(locale) {
  const parts = locale.split('-');
  return parts.length > 1 ? parts[1] : null;
}

// "UK" is the conventional user-facing label for GB; every other region
// subtag is already recognisable as-is. This is display-only — detection
// still derives from the catalogue's locale field.
const REGION_DISPLAY_OVERRIDES = { GB: 'UK' };
function regionDisplayName(region) {
  return REGION_DISPLAY_OVERRIDES[region] || region;
}

function sectionRegions(section) {
  const seen = new Set();
  for (const v of section.voices) {
    const r = extractRegion(v.locale);
    if (r) seen.add(r);
  }
  return [...seen].sort((a, b) => regionDisplayName(a).localeCompare(regionDisplayName(b)));
}

// Voice-sample playback (ticket #746).
// A single shared <audio> element keeps the "only one sample plays at a
// time" semantics trivial — starting a new sample just reassigns src.
// `voicePlayerButton` tracks which row's play button is currently lit so
// we can reset it when playback ends or a different row is clicked.
let voicePlayer = null;
let voicePlayerButton = null;
// Tracks the sample currently loaded on the shared <audio> element so we
// can skip redundant src reassignments (which would abort buffered data).
let voicePreloadedUrl = null;
// Web Audio graph for click-free start/stop. We route the shared <audio>
// element through a GainNode and apply a short ramp on play/stop so the
// output never jumps abruptly between non-zero amplitudes (which the DAC
// renders as an audible tick).
let voiceAudioCtx = null;
let voiceGainNode = null;
let voiceFadePauseTimer = null;
const VOICE_FADE_SECONDS = 0.025;

// Per-session preview speed. Persists across voice and language
// switches (so the user can compare voices at a chosen speed) but
// resets to 1× whenever the cycle reaches its terminal "stop" state
// or the tab reloads.
const VOICE_PLAYBACK_RATES = [1, 1.25, 1.5, 1.75, 2];
let voicePlaybackRateIndex = 0;

const VOICE_ICON_PLAY = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';

function currentVoicePlaybackRate() {
  return VOICE_PLAYBACK_RATES[voicePlaybackRateIndex];
}

function renderVoicePlayingButton(button) {
  // While a sample is playing the button displays the current speed;
  // tapping it advances to the next value, except at the terminal step
  // (last speed) where the next tap stops — the aria-label reflects
  // that so screen-reader users get accurate next-action wording.
  const rate = currentVoicePlaybackRate();
  const atTerminal = voicePlaybackRateIndex === VOICE_PLAYBACK_RATES.length - 1;
  const name = button.dataset.voiceName;
  button.innerHTML = `<span class="voice-play-btn-speed">${rate}×</span>`;
  button.setAttribute('aria-label',
    atTerminal
      ? `Stop ${name} sample`
      : `${name} sample at ${rate}× — tap to change speed`);
}

function advanceVoicePlaybackRate() {
  // The cycle's terminal state is "stop" once we pass the last speed.
  // Reset the index so the next play starts fresh at 1×.
  if (voicePlaybackRateIndex === VOICE_PLAYBACK_RATES.length - 1) {
    voicePlaybackRateIndex = 0;
    stopVoiceSample();
    return;
  }
  voicePlaybackRateIndex += 1;
  if (voicePlayer) voicePlayer.playbackRate = currentVoicePlaybackRate();
  if (voicePlayerButton) renderVoicePlayingButton(voicePlayerButton);
}

function voiceSampleUrl(voiceId, inTier) {
  const suffix = inTier ? '' : '-upsell';
  return `/audio/voice-samples/${voiceId}${suffix}.mp3`;
}

function resetVoicePlayButton() {
  if (!voicePlayerButton) return;
  voicePlayerButton.innerHTML = VOICE_ICON_PLAY;
  voicePlayerButton.setAttribute('aria-label', voicePlayerButton.dataset.playLabel);
  voicePlayerButton.classList.remove('voice-play-btn--playing');
  voicePlayerButton = null;
}

function clearVoiceFadePauseTimer() {
  if (voiceFadePauseTimer) {
    clearTimeout(voiceFadePauseTimer);
    voiceFadePauseTimer = null;
  }
}

function silenceVoiceGainNow() {
  if (!voiceAudioCtx) return;
  const t = voiceAudioCtx.currentTime;
  voiceGainNode.gain.cancelScheduledValues(t);
  voiceGainNode.gain.setValueAtTime(0, t);
}

function rampVoiceGainIn() {
  if (!voiceAudioCtx) return;
  const t = voiceAudioCtx.currentTime;
  voiceGainNode.gain.cancelScheduledValues(t);
  voiceGainNode.gain.setValueAtTime(0, t);
  voiceGainNode.gain.linearRampToValueAtTime(1, t + VOICE_FADE_SECONDS);
}

function rampVoiceGainOut() {
  if (!voiceAudioCtx) return;
  const t = voiceAudioCtx.currentTime;
  // Ramp from the current value (which may itself be mid-ramp) so we don't
  // re-introduce a jump by snapping back up to 1 before fading.
  const current = voiceGainNode.gain.value;
  voiceGainNode.gain.cancelScheduledValues(t);
  voiceGainNode.gain.setValueAtTime(current, t);
  voiceGainNode.gain.linearRampToValueAtTime(0, t + VOICE_FADE_SECONDS);
}

function stopVoiceSample() {
  if (voicePlayer) {
    const finish = () => {
      voicePlayer.pause();
      voicePlayer.removeAttribute('src');
      voicePlayer.load();  // drop any buffered data
      voicePreloadedUrl = null;
      voiceFadePauseTimer = null;
    };
    clearVoiceFadePauseTimer();
    if (voiceAudioCtx) {
      // Fade out before pause() so the cut isn't audible. A pause mid-
      // waveform leaves the output at a non-zero amplitude, which clicks
      // on the next play.
      rampVoiceGainOut();
      voiceFadePauseTimer = setTimeout(finish, VOICE_FADE_SECONDS * 1000 + 10);
    } else {
      finish();
    }
  }
  resetVoicePlayButton();
}

function ensureVoicePlayer() {
  if (voicePlayer) return voicePlayer;
  voicePlayer = new Audio();
  voicePlayer.addEventListener('ended', resetVoicePlayButton);
  voicePlayer.addEventListener('error', () => {
    resetVoicePlayButton();
    showToast('Could not play voice sample.', 'error');
  });
  return voicePlayer;
}

// Lazy: AudioContext needs a user gesture to resume, so we defer creation
// until the first play click. Once routed through Web Audio, the <audio>
// element's normal output is replaced by the graph output, so the gain
// node controls all audible playback. Falls back to plain HTMLMediaElement
// output if Web Audio is unavailable (very old browsers).
function ensureVoiceAudioGraph() {
  if (voiceAudioCtx || !voicePlayer) return;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  try {
    voiceAudioCtx = new Ctx();
    voiceGainNode = voiceAudioCtx.createGain();
    voiceGainNode.gain.value = 0;
    const source = voiceAudioCtx.createMediaElementSource(voicePlayer);
    source.connect(voiceGainNode).connect(voiceAudioCtx.destination);
  } catch {
    voiceAudioCtx = null;
    voiceGainNode = null;
  }
}

// Hover/touch preload: kick off the fetch before the user clicks so the
// buffer is warm by the time they do. Skipped while a sample is already
// playing to avoid clobbering the active src.
function preloadVoiceSample(url) {
  if (voicePlayerButton) return;
  if (voicePreloadedUrl === url) return;
  ensureVoicePlayer();
  voicePlayer.src = url;
  voicePreloadedUrl = url;
  voicePlayer.load();
}

function playVoiceSample(url, button) {
  ensureVoicePlayer();
  ensureVoiceAudioGraph();

  // Tapping the active button advances the speed; past the last speed
  // the cycle ends in "stop". See advanceVoicePlaybackRate.
  if (voicePlayerButton === button) {
    advanceVoicePlaybackRate();
    return;
  }

  resetVoicePlayButton();

  // Silence the graph immediately so reassigning src (which abruptly cuts
  // the previous sample) and the new sample's first frames are inaudible
  // until the fade-in ramps gain back up.
  silenceVoiceGainNow();
  clearVoiceFadePauseTimer();

  voicePlayerButton = button;
  renderVoicePlayingButton(button);
  button.classList.add('voice-play-btn--playing');

  if (voicePreloadedUrl !== url) {
    voicePlayer.src = url;
    voicePreloadedUrl = url;
  }
  voicePlayer.playbackRate = currentVoicePlaybackRate();

  // Guard against stale starts: a second click reassigns voicePlayerButton,
  // so any deferred start() for the previous click must no-op.
  const start = async () => {
    if (voicePlayerButton !== button) return;
    if (voiceAudioCtx && voiceAudioCtx.state === 'suspended') {
      try { await voiceAudioCtx.resume(); } catch {}
    }
    // Rewind in case the sample ended on a prior play and src wasn't
    // reassigned (replaying the same row after natural end).
    voicePlayer.currentTime = 0;
    try {
      await voicePlayer.play();
    } catch {
      if (voicePlayerButton !== button) return;
      resetVoicePlayButton();
      showToast('Could not play voice sample.', 'error');
      return;
    }
    if (voicePlayerButton !== button) return;
    // Ramp in only once playback has actually begun, so the gain envelope
    // aligns with the first audible samples.
    rampVoiceGainIn();
  };

  // HAVE_ENOUGH_DATA: enough buffered to play through without stalling.
  // Without this wait, play() starts at HAVE_FUTURE_DATA and stutters
  // when the buffer drains faster than the network refills it — most
  // noticeable at higher playback rates.
  if (voicePlayer.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
    start();
  } else {
    voicePlayer.addEventListener('canplaythrough', start, { once: true });
  }
}

async function fetchVoiceSettings() {
  const token = await sessionToken();
  if (!token) throw new Error('Not authenticated');
  // apiRequest for the client-identity header + retry; throws on non-2xx,
  // which loadVoiceSettings' caller surfaces as the card's error state.
  const res = await apiRequest('/api/settings', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
}

async function saveVoiceSettings(voicePreferences) {
  const token = await sessionToken();
  if (!token) throw new Error('Not authenticated');
  let res;
  try {
    // The PUT carries the full preferences snapshot, so apiRequest's 5xx
    // retry is safe (idempotent) as well as wanted.
    res = await apiRequest('/api/settings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ voicePreferences }),
    });
  } catch (err) {
    // Prefer the server's own `error` message (validation copy the toast
    // shows verbatim); fall back to the status-stamped message this
    // function has always thrown.
    let message = err.status ? `Settings save failed: ${err.status}` : err.message;
    try {
      const body = JSON.parse(err.body ?? '');
      if (body.error) message = body.error;
    } catch { /* fall through */ }
    throw new Error(message);
  }
  return res.json();
}

// Build the per-voice {enabled} snapshot the server expects (ADR #724
// amendment). The server splits this into voiceDisables / voiceEnables
// using the per-tier `defaultEnabled` flag — clients don't need to know
// which side of the default each voice falls on.
function buildPreferencesFromSettings(settings) {
  const preferences = {};
  for (const { lang, voices } of settings.languages) {
    const inTier = voices.filter((v) => v.inTier);
    if (inTier.length === 0) continue;
    const perVoice = {};
    for (const v of inTier) perVoice[v.voiceId] = v.enabled;
    preferences[lang] = perVoice;
  }
  return preferences;
}

function renderVoicePreferences(settings) {
  voiceSettings = settings;

  // Populate language dropdown. Labels come from the server (see
  // src/language/names.ts) so the catalogue stays the single source of truth.
  $voiceLang.replaceChildren();
  for (const { lang, displayName } of settings.languages) {
    const option = document.createElement('option');
    option.value = lang;
    option.textContent = displayName;
    $voiceLang.appendChild(option);
  }

  // Keep selection sticky across reloads; default to English if present.
  const available = settings.languages.map((l) => l.lang);
  if (!selectedVoiceLang || !available.includes(selectedVoiceLang)) {
    selectedVoiceLang = available.includes('en') ? 'en' : (available[0] ?? null);
  }
  if (selectedVoiceLang) $voiceLang.value = selectedVoiceLang;

  renderVoiceList();
  $voicesCard.dataset.state = 'loaded';
}

function renderVoiceList() {
  // Re-rendering drops the old button DOM, so cancel any in-flight sample
  // playback — otherwise the stop icon disappears but audio keeps going.
  stopVoiceSample();

  // replaceChildren() momentarily empties the list, which breaks scroll
  // anchoring on toggle clicks (the page jumps and the card visibly
  // shifts). Snapshot and restore the document scroll position around
  // the rebuild so this stays the single source of truth for row layout.
  const scrollY = window.scrollY;

  $voiceList.replaceChildren();
  try {
    if (!voiceSettings || !selectedVoiceLang) return;

    const section = voiceSettings.languages.find((l) => l.lang === selectedVoiceLang);
    if (!section) return;

    const regions = sectionRegions(section);

    // Single-region languages (most of the catalogue) render as a flat list
    // — region headers would just be visual noise. Multi-region languages
    // (en, es, fr, pt) group voices into per-region concertinas (default
    // closed) with a single bulk-toggle and an "X of Y enabled" hint on the
    // title row so users can see the regional state without expanding every
    // section.
    if (regions.length < 2) {
      for (const voice of section.voices) {
        $voiceList.appendChild(buildVoiceRow(voice));
      }
      return;
    }

    for (const region of regions) {
      const regionVoices = section.voices.filter((v) => extractRegion(v.locale) === region);
      $voiceList.appendChild(buildRegionGroup(region, regionVoices));
    }
    // Defensive: any voice without a region subtag (none in the current
    // catalogue for multi-region languages, but guard anyway) appears at
    // the bottom without a header.
    for (const voice of section.voices) {
      if (extractRegion(voice.locale) === null) {
        $voiceList.appendChild(buildVoiceRow(voice));
      }
    }
  } finally {
    if (window.scrollY !== scrollY) window.scrollTo(0, scrollY);
  }
}

function buildVoiceRow(voice) {
  const li = document.createElement('li');
  li.className = 'voice-row' + (voice.inTier ? '' : ' voice-upsell');
  // The row's own identity, the same way queue rows carry `data-id`. Everything
  // else on the row is user-facing copy that may be rewritten freely — the
  // display name, and the locale/gender subtitle — so this is the only handle
  // that means the same thing here, in config/tiers.json, and in the audio key
  // the pipeline files the episode under.
  li.dataset.voiceId = voice.voiceId;

  // In-tier voices play a standard sample; locked (upsell) rows play
  // the upsell sample. URL is derived from the voice id alone — no
  // API round-trip.
  //
  // The row's controls are named from the display name AND the locale: a
  // display name is not unique within a language (en-GB's and en-US's
  // "George" are both English), and a doubled name is two controls a screen
  // reader cannot tell apart (docs/constraints/accessible-names.md).
  const spokenName = `${voice.displayName} (${voice.locale})`;
  const playBtn = document.createElement('button');
  playBtn.type = 'button';
  playBtn.className = 'voice-play-btn';
  const playLabel = `Play ${spokenName} sample`;
  playBtn.dataset.playLabel = playLabel;
  playBtn.dataset.voiceName = spokenName;
  playBtn.setAttribute('aria-label', playLabel);
  playBtn.innerHTML = VOICE_ICON_PLAY;
  const sampleUrl = voiceSampleUrl(voice.voiceId, voice.inTier);
  playBtn.addEventListener('click', () => playVoiceSample(sampleUrl, playBtn));
  // Warm the buffer ahead of the click. pointerenter covers mouse/pen;
  // touchstart covers touch (pointerenter doesn't fire reliably there).
  playBtn.addEventListener('pointerenter', () => preloadVoiceSample(sampleUrl));
  playBtn.addEventListener('touchstart', () => preloadVoiceSample(sampleUrl), { passive: true });
  li.appendChild(playBtn);

  const meta = document.createElement('span');
  meta.className = 'voice-meta';
  const name = document.createElement('span');
  name.className = 'voice-name';
  name.textContent = voice.displayName;
  meta.appendChild(name);
  const sub = document.createElement('span');
  sub.className = 'voice-sub';
  const subParts = [voice.locale];
  if (voice.gender) subParts.push(voice.gender);
  sub.textContent = subParts.join(' • ');
  meta.appendChild(sub);
  li.appendChild(meta);

  if (voice.inTier) {
    const label = document.createElement('label');
    label.className = 'voice-toggle';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    // The face is a slider with no text, so the name comes from the row
    // (docs/constraints/accessible-names.md) — beside the row's "Play <name>
    // sample" button, and the region's "Enable all <region> voices".
    checkbox.setAttribute('aria-label', `Enable ${spokenName}`);
    checkbox.checked = voice.enabled;
    checkbox.addEventListener('change', () => toggleVoice(voice.voiceId, checkbox.checked));
    label.appendChild(checkbox);
    const slider = document.createElement('span');
    slider.className = 'voice-slider';
    label.appendChild(slider);
    li.appendChild(label);
  } else {
    const badge = document.createElement('span');
    badge.className = 'voice-upsell-badge';
    // A neutral state label, not an upgrade CTA: it marks the voice as
    // locked to a higher tier without a call to action or purchase link, so
    // it reads the same on the web (where the tier picker does the upselling)
    // and inside the app shells where 3.1.1 forbids steering copy (#2590).
    badge.textContent = 'Locked';
    li.appendChild(badge);
  }

  return li;
}

const VOICE_REGION_CHEVRON = '<svg class="voice-region-chevron" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 6l6 6-6 6z"/></svg>';

function buildRegionGroup(region, regionVoices) {
  const li = document.createElement('li');
  li.className = 'voice-region';

  const inTier = regionVoices.filter((v) => v.inTier);
  const enabledHere = inTier.filter((v) => v.enabled).length;
  const allEnabled = inTier.length > 0 && enabledHere === inTier.length;
  const label = regionDisplayName(region);
  const isOpen = voiceRegionOpen.get(regionOpenKey(region)) ?? false;
  li.dataset.open = String(isOpen);
  const bodyId = `voice-region-body-${selectedVoiceLang}-${region}`;

  // The header row holds a toggle <button> on the left (chevron + label +
  // count) and the all/none action <button>s as siblings on the right.
  // Keep them siblings — nesting <button>s is invalid HTML and breaks the
  // a11y tree.
  const header = document.createElement('div');
  header.className = 'voice-region-header-row';

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'voice-region-header';
  toggle.setAttribute('aria-expanded', String(isOpen));
  toggle.setAttribute('aria-controls', bodyId);
  toggle.innerHTML = VOICE_REGION_CHEVRON;

  const name = document.createElement('span');
  name.className = 'voice-region-header-name';
  name.textContent = label;
  toggle.appendChild(name);

  // aria-live so screen readers hear "6 of 10 enabled" after clicking
  // all/none or a per-voice toggle.
  const count = document.createElement('span');
  count.className = 'voice-region-count';
  count.setAttribute('aria-live', 'polite');
  count.textContent = inTier.length === 0
    ? `${regionVoices.length} locked`
    : `${enabledHere} of ${inTier.length} enabled`;
  toggle.appendChild(count);

  toggle.addEventListener('click', () => {
    voiceRegionOpen.set(regionOpenKey(region), !isOpen);
    renderVoiceList();
  });
  header.appendChild(toggle);

  // Locked region (no in-tier voices) — skip the bulk action. The voice
  // rows below still show their Locked badge, so the locked state is
  // already communicated per voice.
  //
  // Cycle button: one label/action that flips between "all" and "none"
  // depending on the region's current state. In a mixed state it offers
  // "all" so a single click always reaches a clean state. The language-
  // level "at least one voice on" invariant is enforced post-toggle by
  // auto-restoring the configured default voice — see toggleRegionVoices
  // — so this button is never disabled.
  if (inTier.length > 0) {
    const cycle = document.createElement('button');
    cycle.type = 'button';
    cycle.className = 'voice-region-action';
    const nextEnabled = !allEnabled;
    cycle.textContent = nextEnabled ? 'all' : 'none';
    cycle.setAttribute(
      'aria-label',
      `${nextEnabled ? 'Enable' : 'Disable'} all ${label} voices`,
    );
    cycle.addEventListener('click', () => toggleRegionVoices(region, nextEnabled));
    header.appendChild(cycle);
  }

  li.appendChild(header);
  li.appendChild(buildRegionBody(bodyId, regionVoices));
  return li;
}

function buildRegionBody(bodyId, regionVoices) {
  const body = document.createElement('ul');
  body.className = 'voice-region-body';
  body.id = bodyId;
  for (const voice of regionVoices) {
    body.appendChild(buildVoiceRow(voice));
  }
  return body;
}

// Single-flight save queue. Rapid clicks on per-voice toggles and on the
// region all/none buttons all mutate voiceSettings optimistically, then
// enqueue a save. At most one PUT is in flight — later clicks just
// overwrite the pending payload, so the server only ever sees the latest
// intent and can't observe out-of-order writes. On error we drop the
// queue and refetch authoritative state, since coalesced clicks can't be
// individually rolled back.
let voiceSavePending = null;
let voiceSaveRunning = false;

async function flushVoiceSaves() {
  if (voiceSaveRunning) return;
  voiceSaveRunning = true;
  try {
    while (voiceSavePending) {
      const payload = voiceSavePending;
      voiceSavePending = null;
      try {
        const updated = await saveVoiceSettings(payload);
        // Only render from this response if no newer click is queued —
        // otherwise the next iteration will send it and render then.
        if (!voiceSavePending) renderVoicePreferences(updated);
      } catch (err) {
        console.error('Failed to save voice settings:', err);
        showToast(err.message || 'Failed to save voice settings', 'error');
        voiceSavePending = null;
        try {
          renderVoicePreferences(await fetchVoiceSettings());
        } catch (refetchErr) {
          console.error('Failed to reload voice settings:', refetchErr);
        }
      }
    }
  } finally {
    voiceSaveRunning = false;
  }
}

function queueVoiceSave() {
  voiceSavePending = buildPreferencesFromSettings(voiceSettings);
  flushVoiceSaves();
}

function toggleRegionVoices(region, nextEnabled) {
  if (!voiceSettings || !selectedVoiceLang) return;
  const section = voiceSettings.languages.find((l) => l.lang === selectedVoiceLang);
  if (!section) return;

  for (const v of section.voices) {
    if (!v.inTier) continue;
    if (extractRegion(v.locale) !== region) continue;
    v.enabled = nextEnabled;
  }
  ensureLanguageHasEnabledVoice(section);
  queueVoiceSave();
  renderVoiceList();
}

function toggleVoice(voiceId, nextEnabled) {
  if (!voiceSettings || !selectedVoiceLang) return;
  const section = voiceSettings.languages.find((l) => l.lang === selectedVoiceLang);
  if (!section) return;
  const voice = section.voices.find((v) => v.voiceId === voiceId);
  if (!voice) return;

  voice.enabled = nextEnabled;
  ensureLanguageHasEnabledVoice(section);
  queueVoiceSave();
  // No re-render: the browser already flipped the clicked checkbox and
  // the CSS slider transition is running. Region counts, the cycle
  // button label, and any auto-restored voice catch up on the post-save
  // renderVoiceList. (Region all/none still re-renders synchronously —
  // see toggleRegionVoices.)
}

// Server validation rejects zero-enabled languages, and so does the UI:
// if a toggle empties the language we silently re-enable the configured
// default voice (`config/default-voices.json`, surfaced as
// `section.defaultVoiceId`) and surface a toast so the user knows why
// their click didn't stick. Falls back to the first in-tier voice if no
// default is configured for the language.
function ensureLanguageHasEnabledVoice(section) {
  const inTier = section.voices.filter((v) => v.inTier);
  if (inTier.length === 0) return;
  if (inTier.some((v) => v.enabled)) return;

  const fallback = (section.defaultVoiceId
    && inTier.find((v) => v.voiceId === section.defaultVoiceId))
    ?? inTier[0];
  if (!fallback) return;
  fallback.enabled = true;
  showToast(
    `At least one ${section.displayName} voice must stay on — kept ${fallback.displayName} enabled.`,
    'info',
  );
}

async function loadVoiceSettings() {
  if (!$voicesCard) return;
  const settings = await fetchVoiceSettings();
  renderVoicePreferences(settings);
}

// ---------------------------------------------------------------------------
// Save articles (issue #1652)
//
// The always-available reference for every way to send an article to
// podstow — the standing counterpart to the one-time onboarding card.  The
// method *content* (title, sub-label, description, icon, availability) is the
// shared source of truth in clients/shared/onboarding/save-methods.js; this
// renders a row per method and owns the per-surface action wiring:
//   - paste        → the app's own link in a copy-field, plus an OS share
//                    button where the Web Share API exists and a QR of the
//                    link on desktop (see buildWebAppLinkField)
//   - ai           → the AI Assistants settings section (in-page #mcp); the
//                    row is omitted when that section isn't configured on
//                    this deployment, so the cross-link never dead-ends
// 'coming-soon' methods render a muted chip with no link. Every method is live
// today — #1643 / #1841 / #1644 flipped the last of them as the extension and
// the native apps shipped — so the state is dormant, not gone.
// ---------------------------------------------------------------------------

// Where the SPA serves the shared store-badge artwork from —
// clients/web/public/shared/badges is a symlink to clients/shared/badges
// (the single source of truth), the same pattern as shared/fonts + shared/icons.
const BADGE_BASE = '/shared/badges/';

// Build one store badge from a shared `stores` entry (save-methods.js):
//   - live listing        → the store's own official badge, linked to the
//                           listing in a guarded new tab.
//   - coming-soon listing → a dimmed, non-interactive badge with a "Coming
//                           soon" caption; no store is in this state today.
// `data-badge` (the artwork basename, sans extension) drives the per-badge size
// tuning in CSS, since each store bakes a different amount of clear space.
function buildStoreBadge(store) {
  const { badge } = store;
  const slug = badge.file.replace(/\.[^.]+$/, '');

  const img = document.createElement('img');
  img.src = `${BADGE_BASE}${badge.file}`;
  // Intrinsic dimensions reserve the box (CLS); the rendered height is set in
  // CSS, preserving each badge's aspect ratio.
  img.width = badge.width;
  img.height = badge.height;
  img.alt = badge.alt;
  img.loading = 'lazy';
  img.decoding = 'async';

  if (store.status === 'coming-soon') {
    // Not a link — nothing to open yet. The relatively-positioned wrap hangs the
    // caption below the badge out of flow, so the badge itself stays on the
    // row's shared centre line with the live ones.
    const wrap = document.createElement('span');
    wrap.className = 'save-method-badge-soon-wrap';
    const badgeEl = document.createElement('span');
    badgeEl.className = 'save-method-badge save-method-badge-soon';
    badgeEl.dataset.badge = slug;
    badgeEl.appendChild(img);
    const caption = document.createElement('span');
    caption.className = 'save-method-badge-soon-caption';
    caption.textContent = 'Coming soon';
    wrap.append(badgeEl, caption);
    return wrap;
  }

  const a = document.createElement('a');
  a.className = 'save-method-badge';
  a.dataset.badge = slug;
  a.href = store.url;
  a.target = '_blank';
  a.rel = 'noopener';
  a.appendChild(img);
  return a;
}

// The web-app row's action: the app's own link in a shared copy-field (the
// same pattern as the MCP server URL above it). The reader is already inside
// the web app, so the old "Open → home" button offered nothing; the row's
// promise is "on every device", and the actionable half of that is getting
// the link onto the *other* devices. The link is the deployment's own origin,
// so dev / staging copies stay honest. Two independently-gated hand-off
// gestures then join the field:
//   - a share button, where the Web Share API exists — feature-detected, so
//     browsers without navigator.share (desktop Firefox/Chrome) never see a
//     dead button;
//   - a QR of the link beneath the field on desktop (UA-sniffed), mirroring
//     the feed panel's block. It encodes the origin directly, not an open.*
//     trampoline: Android App Links already route the bare origin into the
//     native app when installed, and the SPA's install banner carries the
//     store nudge otherwise.
// The gates are independent by design: a desktop browser that exposes
// navigator.share (Safari, Edge) gets both — its share sheet (AirDrop and
// friends) and the QR are each genuinely useful there.
function buildWebAppLinkField() {
  const url = `${location.origin}/`;

  const field = buildCopyField(url, {
    inputLabel: 'Web app link',
    buttonLabel: 'Copy web app link',
    announce: 'Web app link copied',
    onCopy: (ok) => { if (!ok) showToast('Could not copy.', 'error'); },
  });

  if (navigator.share) {
    const shareBtn = document.createElement('button');
    shareBtn.className = 'btn btn-outline';
    shareBtn.type = 'button';
    shareBtn.setAttribute('aria-label', 'Share web app link');
    shareBtn.innerHTML = SHARE_ICON_SVG;
    // No clipboard fallback here — the copy button beside it already owns that
    // gesture, so the shared wrapper's fallback is opted out (fallback: false).
    shareBtn.addEventListener('click', () => { shareLink(url, { title: 'podstow', fallback: false }); });
    field.appendChild(shareBtn);
  }

  // The canonical caller-side QR gate (see buildQrBlock's JSDoc): show the QR
  // on desktop AND tablets (a big screen a separate phone can scan), but not on
  // a handset — a phone would be scanning a code off its own screen.
  if (showsScanQr(navigator.userAgent, navigator.maxTouchPoints)) {
    const frag = document.createDocumentFragment();
    frag.appendChild(field);
    frag.appendChild(buildQrBlock(url, {
      loadQRCode,
      // No lead: the "Web app" card heading and its copy already frame the
      // code, so a QR lead here would only restate them. (The feed panel's
      // subscribe hero still leads with "Listen on your mobile device" — there
      // the QR is the hero and needs its own label.)
    }));
    return frag;
  }

  return field;
}

// The live action for a method, rendered full-width below the prose:
//   - store methods (extension, mobile) → a wrapping row of the stores' own
//     branded badges (#1643 Chrome, #1841 Firefox, #1644 the native apps).
//   - paste                             → the copy/share link field above —
//     on desktop a DocumentFragment (field + QR block), so don't touch
//     properties of the return value; append it and style via classes.
//   - ai                                → a single text button.
// Returns null for any method without explicit wiring (coming-soon methods show
// a chip on the title line instead).
function buildSaveMethodAction(method) {
  if (method.stores?.length) {
    // Store links + artwork ride on the shared method (save-methods.js) — the
    // destinations are identical on every surface — rather than being wired
    // per-surface here. One badge per listing, sharing the action row.
    const wrap = document.createElement('div');
    wrap.className = 'save-method-badges';
    if (method.stores.some((s) => s.status === 'coming-soon')) {
      // Reserve room below the row for the coming-soon caption (out of flow) so
      // it can't collide with the card's lower edge.
      wrap.classList.add('save-method-badges--has-soon');
    }
    for (const store of method.stores) wrap.appendChild(buildStoreBadge(store));
    return wrap;
  }

  if (method.id === 'paste') return buildWebAppLinkField();

  if (method.id === 'ai') {
    const a = document.createElement('a');
    a.className = 'btn btn-outline btn-compact save-method-action';
    a.href = '#mcp';
    a.textContent = 'Set up';
    return a;
  }

  return null;
}

// Build a single method row.  Returns null for methods that have nothing to
// show on this deployment (the AI cross-link with no AI Assistants section).
//
// Layout: an icon-beside-title header, then the sub-label, description and
// actions stacked full-width beneath it.  Keeping the icon to the header line
// (rather than a tall left column) lets the prose and store badges use the
// row's full width instead of being penned into a narrow column on the right.
function buildSaveMethodRow(method, { mcpAvailable }) {
  if (method.id === 'ai' && !mcpAvailable) return null;

  const li = document.createElement('li');
  li.className = 'save-method';
  li.dataset.method = method.id;
  const comingSoon = method.status === 'coming-soon';
  if (comingSoon) li.dataset.status = 'coming-soon';

  const header = document.createElement('div');
  header.className = 'save-method-header';

  const icon = document.createElement('span');
  icon.className = 'save-method-icon';
  icon.setAttribute('aria-hidden', 'true');
  // method.icon is source-controlled inline SVG markup (no user input).
  icon.innerHTML = `<svg viewBox="0 0 24 24" focusable="false">${method.icon}</svg>`;
  header.appendChild(icon);

  const titleRow = document.createElement('div');
  titleRow.className = 'save-method-title-row';
  const title = document.createElement('span');
  title.className = 'save-method-title';
  title.textContent = method.title;
  titleRow.appendChild(title);
  // A whole-method "Coming soon" rides the title line, pushed to the right edge.
  if (comingSoon) {
    const chip = document.createElement('span');
    chip.className = 'save-method-soon';
    chip.textContent = 'Coming soon';
    titleRow.appendChild(chip);
  }
  header.appendChild(titleRow);
  li.appendChild(header);

  // Platform sub-label, full-width beneath the header.
  if (method.meta) {
    const meta = document.createElement('span');
    meta.className = 'save-method-meta';
    meta.textContent = method.meta;
    li.appendChild(meta);
  }

  const desc = document.createElement('p');
  desc.className = 'save-method-desc';
  desc.textContent = method.description;
  li.appendChild(desc);

  // Live methods carry their action below the prose, left-aligned.
  if (!comingSoon) {
    const action = buildSaveMethodAction(method);
    if (action) li.appendChild(action);
  }

  return li;
}

// Render the flat method list.  The AI cross-link is gated on the AI
// Assistants section actually being present (mcp-card hidden at boot where
// no PODSTOW_MCP_URL is configured) so it never points at a missing section.
function renderSaveMethods() {
  const list = document.getElementById('save-methods');
  if (!list) return;
  const mcpCard = document.getElementById('mcp-card');
  const mcpAvailable = !!mcpCard && !mcpCard.hidden;
  const frag = document.createDocumentFragment();
  for (const method of SAVE_METHODS) {
    const row = buildSaveMethodRow(method, { mcpAvailable });
    if (row) frag.appendChild(row);
  }
  list.replaceChildren(frag);
}

// ---------------------------------------------------------------------------
// Settings as separate "screens" — iOS Settings-style navigation
//
// One section is visible at a time.  Mobile starts on a list-of-sections
// "home" view; tapping a row drills in to that section's content with a
// back button.  Desktop shows the list as a persistent sidebar next to
// the active section's content.
//
// The URL hash (#feed, #voices, …) is the single source of truth for
// which section is active.  Hash empty = mobile list view (or desktop
// auto-default).  All navigation routes through the hash so the browser
// back/forward buttons stay in sync; `hashchange` is the only place we
// rerender from.
//
// All sidebar entries appear up-front — sections that fetch their data
// asynchronously (subscription / apps / voices) start in a loading
// state inside their card (see `data-state` on the section elements
// and the matching CSS rules), so the sidebar never jumps as loads
// complete.
//
// The rows themselves are markup (settings/index.html), not built here:
// this module is deferred, and on mobile the list IS the settings screen,
// so a JS-built list arrives after the home -> settings slide has already
// carried a blank screen across.  The page draws its own first frame and
// we adopt it — same rows, same active section — so this runs as
// hydration rather than a repaint.  A row's slug names both its href and
// its section (`#<slug>` -> `#<slug>-card`).
// ---------------------------------------------------------------------------
// `apps-card` doesn't get its own nav entry — it's a sub-area of the
// AI Assistants section, paired via `data-settings-section="mcp"` in
// the HTML and activated together by `showSection` below.

const DESKTOP_QUERY = '(min-width: 1024px)';
const MOTION_QUERY  = '(prefers-reduced-motion: no-preference)';

function setupSettingsNav() {
  const shell    = document.getElementById('settings-shell');
  const navList  = document.getElementById('settings-nav-list');
  const backBtn  = document.getElementById('settings-back-btn');
  const title    = document.getElementById('settings-section-title');
  if (!shell || !navList || !backBtn) return;

  // Track the input modality so the focus we move programmatically on
  // drill-in / back only paints the :focus-visible ring for keyboard users.
  // WebKit (the engine behind the macOS + iOS wrappers and Safari) flags
  // every element.focus() as focus-visible regardless of how the navigation
  // was triggered — Chromium infers it from the preceding interaction — so a
  // *tap* on a list row would otherwise ring the back chevron this moves
  // focus to. We record the modality ourselves and only suppress the ring
  // when the last interaction wasn't the keyboard; a genuine keyboard nav
  // still gets its indicator (WCAG 2.4.7). Boot (no interaction) counts as
  // non-keyboard, so a mobile deep-link load doesn't ring either. The
  // `focusVisible` option is a hint browsers that lack it ignore — harmless,
  // since their heuristic already suppresses the ring for pointer input.
  let lastInputKeyboard = false;
  document.addEventListener('keydown',     () => { lastInputKeyboard = true;  });
  document.addEventListener('pointerdown', () => { lastInputKeyboard = false; });
  const focusForNav = (el) =>
    el.focus(lastInputKeyboard ? undefined : { focusVisible: false });

  // Adopt the rows the page painted.  A card whose `hidden` attribute is
  // set at this point (mcp-card, hidden at boot where no MCP URL is
  // configured) is a deploy-time decision, so drop its row rather than
  // offering a section that leads nowhere — the card stays the single
  // answer to "does this deployment have that section", and the row
  // follows it.
  const entries = [];
  for (const link of navList.querySelectorAll('a[data-slug]')) {
    const slug = link.dataset.slug;
    const card = document.getElementById(`${slug}-card`);
    const li = link.parentElement;
    if (li) li.hidden = !card || card.hidden;
    if (!card || card.hidden) continue;
    entries.push({ card, slug, label: link.textContent, link });
  }
  if (entries.length === 0) return;

  const entryBySlug = new Map(entries.map((e) => [e.slug, e]));

  const setTitle = (entry) => {
    if (!title) return;
    title.replaceChildren(document.createTextNode(entry.label));
  };

  // Companion elements (apps-card + its heading, in the AI Assistants
  // section) opt into the section via data-settings-section.  Looked up
  // once here so showSection/showList don't re-query on every nav.
  const companions = Array.from(
    document.querySelectorAll('[data-settings-section]'),
  );

  const showSection = (entry, opts = {}) => {
    const wasList = shell.dataset.view === 'list';
    // Is this a move, or a re-render of the section already on screen?  The
    // page paints its own first frame (settings/index.html) and this module
    // hydrates it a beat — on a phone, a second or more — later, and a
    // breakpoint cross re-renders in place too.  Neither is a navigation,
    // and only a navigation may touch the scroll offset.
    const wasShowing = entry.card.dataset.active === 'true';
    shell.dataset.view = 'section';
    setTitle(entry);
    for (const e of entries) {
      if (e === entry) e.card.dataset.active = 'true';
      else             delete e.card.dataset.active;
      e.link.classList.toggle('is-active', e === entry);
      if (e === entry) e.link.setAttribute('aria-current', 'true');
      else             e.link.removeAttribute('aria-current');
    }
    for (const el of companions) {
      if (el.dataset.settingsSection === entry.slug) el.dataset.active = 'true';
      else                                           delete el.dataset.active;
    }
    // The upgrade card is one of those companions, so navigating to
    // Subscription can be the moment it becomes visible (#2914).
    maybeEmitPricingView();
    // Top of the new section, not wherever the last one was scrolled to —
    // but only when the section is actually new.  Re-rendering the one
    // already on screen would otherwise throw away the reader's scroll
    // position, or the browser's restored one, on the way in.
    if (!wasShowing) window.scrollTo({ top: 0, behavior: 'auto' });
    // Move focus off the now-hidden list when drilling in on mobile —
    // otherwise the clicked link sits in a display:none subtree and
    // keyboard focus is dropped on the floor.  Desktop keeps the
    // sidebar visible so the link still owns focus there.
    if (opts.moveFocus && wasList && !matchMedia(DESKTOP_QUERY).matches) {
      focusForNav(backBtn);
    }
  };

  const showList = (opts = {}) => {
    // Remember which row was active so we can restore focus to it
    // when the user returns to the list (mobile back).  Desktop keeps
    // the sidebar visible, so its focus already lives there.
    const prevActive = entries.find((e) => e.card.dataset.active === 'true');
    shell.dataset.view = 'list';
    for (const e of entries) {
      delete e.card.dataset.active;
      e.link.classList.remove('is-active');
      e.link.removeAttribute('aria-current');
    }
    for (const el of companions) delete el.dataset.active;
    if (opts.moveFocus && prevActive && !matchMedia(DESKTOP_QUERY).matches) {
      focusForNav(prevActive.link);
    }
  };

  // ── Drilling in is a screen change, so it slides ────────
  // On a phone the list and a section are two screens, not two halves of one,
  // and moving between them used to hard-cut while the navigation one level up
  // — home → settings — slid.  A same-document view transition travels them
  // instead, tagged with the SAME types that navigation uses: `forward` going
  // deeper, `back` coming up.  style.css keys its slide off those types, so
  // there is one definition of the move, and the section header pairing with
  // the wordmark bar under the shared `app-bar` name parks the bar while the
  // screens travel beneath it, exactly as on the cross-document slide.
  //
  // Only across that boundary, and only on the phone layout.  Desktop shows
  // both columns at once, so choosing a section changes what the right-hand
  // column holds and no screen covers another.  A re-render of the view
  // already showing — this module hydrating the frame the page painted, a
  // breakpoint cross — is not a navigation, and section → section, which only
  // a history traverse can reach here, has no side to travel from: untyped is
  // the UA crossfade the sheet leaves as its fallback everywhere else.
  //
  // `ViewTransitionTypeSet` is the feature test rather than the method: every
  // root-snapshot rule in the sheet is type-scoped, so a browser with
  // transitions but no types would cross-fade the two screens instead of
  // sliding them.  The hard cut it has today is the honest fallback.
  const canSlide = () =>
    typeof document.startViewTransition === 'function'
    && typeof window.ViewTransitionTypeSet === 'function'
    && !matchMedia(DESKTOP_QUERY).matches
    && matchMedia(MOTION_QUERY).matches;

  // The slide currently holding the pinned chrome frozen, so one the browser
  // skipped for a newer one can't hand the bars back under its successor.
  let sliding = null;

  const slide = (type, paint) => {
    // Chrome captures a `position: sticky` element from its UNPINNED layout
    // box, so a section scrolled 800px down is captured with its header 800px
    // above the screen.  The shell page's inline script owns that measurement
    // for the cross-document slide (settings/index.html) and hands it out
    // here; this is that fix reused, not a second one.
    const chrome = window._pinnedChrome;
    const token = {};
    sliding = token;
    const release = () => {
      if (sliding !== token) return;
      sliding = null;
      chrome?.thaw();
    };
    const update = () => {
      paint();
      // Measure again now the incoming screen is rendered: the two views pin
      // DIFFERENT elements — the wordmark bar on the list, the section header
      // inside a section — and the new half of a same-document transition is
      // live, not a snapshot, so a bar left frozen at the outgoing view's
      // offset is seen sitting wrong for the whole slide.
      if (sliding !== token) return;
      chrome?.thaw();
      chrome?.freeze();
    };
    chrome?.freeze();
    try {
      document.startViewTransition({ types: [type], update }).finished.then(release, release);
    } catch {
      // A transition that never started must not cost the user the screen
      // they asked for.
      release();
      paint();
    }
  };

  const renderFromHash = ({ moveFocus = false, travel = true } = {}) => {
    const slug = location.hash.replace(/^#/, '');
    let entry = entryBySlug.get(slug);

    // No hash (or unknown slug): mobile shows the list; desktop has
    // no list-only view, so auto-pick the first section and
    // replaceState so refreshes don't bounce back to "no selection".
    if (!entry && matchMedia(DESKTOP_QUERY).matches) {
      entry = entries[0];
      if (location.hash !== `#${entry.slug}`) {
        history.replaceState(null, '', `#${entry.slug}`);
      }
      moveFocus = false;
    }

    const paint = entry
      ? () => showSection(entry, { moveFocus })
      : () => showList({ moveFocus });

    const from = shell.dataset.view;
    const to   = entry ? 'section' : 'list';
    const type = from === 'list' && to === 'section' ? 'forward'
      : from === 'section' && to === 'list' ? 'back'
        : '';

    if (travel && type && canSlide()) slide(type, paint);
    else paint();
  };

  // Click on a list row → push a history entry (enables back-to-list
  // on mobile) and render directly so we can pass `moveFocus: true`
  // — pushState doesn't fire hashchange, so no double-render.  The
  // `fromList` marker lets the in-page back button pop this entry
  // instead of pushing a new one (otherwise every drill-in + back
  // leaves a pair of stale entries that swipe-back replays).
  navList.addEventListener('click', (e) => {
    const link = e.target.closest('a[data-slug]');
    if (!link) return;
    e.preventDefault();
    const slug = link.dataset.slug;
    if (location.hash === `#${slug}`) return;
    history.pushState({ fromList: true }, '', `#${slug}`);
    renderFromHash({ moveFocus: true });
  });

  // In-page back button (mobile only).  If we drilled in from the
  // list, pop that entry so swipe-back doesn't replay every visited
  // section.  Deep-linked entries have no list entry to return to,
  // so push a fresh one — back still lands the user on the list.
  // The flag carries `moveFocus` across history.back()'s async
  // hashchange so focus returns to the previously active row.
  let pendingMoveFocus = false;
  backBtn.addEventListener('click', () => {
    if (!location.hash) return;
    if (history.state?.fromList) {
      pendingMoveFocus = true;
      history.back();
    } else {
      history.pushState(null, '', location.pathname + location.search);
      renderFromHash({ moveFocus: true });
    }
  });

  // hashchange covers browser back/forward across a hash change (a row click
  // renders directly, since pushState fires no hashchange).  Every history
  // change here also changes the hash, so listening to popstate too would
  // render twice.
  // The page's first-frame script has been answering hash changes on its own
  // while this module loaded (settings/index.html); from here they are ours,
  // with the focus handling and the history marker it has no business
  // reproducing.
  window._settingsNavReady = true;
  window.addEventListener('hashchange', () => {
    const moveFocus = pendingMoveFocus;
    pendingMoveFocus = false;
    renderFromHash({ moveFocus });
  });
  // Crossing the desktop breakpoint changes what the same hash should
  // show — e.g. resizing a no-hash mobile list into desktop's two-
  // column layout leaves an empty content area until we re-render
  // and auto-default to a section.
  matchMedia(DESKTOP_QUERY).addEventListener('change', () => renderFromHash());

  // Boot adopts the URL; it does not travel to it.  There is no screen behind
  // this one to come from, and the frame the page left behind cannot be taken
  // as the screen the reader was on: the first-frame script bails for a
  // logged-out visitor (settings/index.html), leaving the markup's list under
  // a `#<slug>` deep link, and a card this deployment hid (#mcp-card without
  // an MCP URL) drops its entry here after that script painted the section.
  // Either disagreement would slide the screen the reader is looking at
  // sideways on arrival.
  renderFromHash({ travel: false });
}

// ---------------------------------------------------------------------------
// Leaving settings by the wordmark
//
// The wordmark is a link to "/", so tapping it pushed a third history entry
// and built home from scratch: the queue refetched, the scroll position lost,
// and the browser's own back button then landing on settings again.
//
// Going home from settings is a dismissal, not a new destination.  So when
// there is a home entry behind us in this tab, travel to it.  The browser
// then restores the home document it still has — its queue, its scroll offset
// and its player, intact and immediately, which is what the home → settings
// slide's counterpart should carry — and the history stack ends the length it
// was before the user went into settings.
//
// It stays an `<a href="/">`: middle-click, open-in-new-tab, right-click and
// a browser without the Navigation API all keep working, and every case the
// decision can't answer falls back to the plain navigation the link has
// always done.
// ---------------------------------------------------------------------------

/** Is `raw` the home screen — as a place to return to, not merely the path?
 *  /?url=… and /?text=… are the share takeover over home (#1989) and ?source=
 *  a satellite handoff: flows the user has finished, not a screen to go back
 *  to.  /index.html counts, since that is where the installed app launches. */
function isHomeEntryUrl(raw, origin) {
  if (!raw) return false;
  let url;
  try { url = new URL(raw); } catch { return false; }
  if (url.origin !== origin || url.search) return false;
  return url.pathname === '/' || url.pathname === '/index.html';
}

/**
 * The key of the nearest home entry BEHIND the current one, or null when
 * there is none and the wordmark's href should just navigate.
 *
 * Behind, never ahead: a home entry in front was reached by pressing back to
 * leave it. Nearest, and skipping whatever lies between, because each section
 * drill-in pushes its own `#slug` entry — stepping back one would land on the
 * section just left rather than on home.
 */
function homeEntryToTravelTo(nav, origin) {
  try {
    const entries = nav?.entries?.();
    const at = nav?.currentEntry?.index;
    if (!Array.isArray(entries) || typeof at !== 'number') return null;
    for (let i = at - 1; i >= 0; i--) {
      if (isHomeEntryUrl(entries[i]?.url, origin)) return entries[i].key;
    }
    return null;
  } catch {
    // An unreadable history is the link's case, not a crash.
    return null;
  }
}

function setupHomeLink() {
  const link = document.querySelector('.header-home');
  if (!link) return;
  link.addEventListener('click', (e) => {
    // Anything the browser treats specially — a new tab, a new window, a
    // download, a middle-click — is not a dismissal; leave it to the href.
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const key = homeEntryToTravelTo(window.navigation, location.origin);
    if (!key) return;

    e.preventDefault();
    const traversal = window.navigation.traverseTo(key);
    // A cross-document traversal never settles these here (this document is
    // frozen or discarded), so they are only ever handled when the traversal
    // fails to start — an evicted entry. Fall back to the navigation the
    // click would have done rather than leaving a dead wordmark.
    traversal?.committed?.catch(() => { location.href = link.href; });
    traversal?.finished?.catch(() => {});
  });
}

// ---------------------------------------------------------------------------
// Logout — clears feed ID (avoid leaking one user's feed to the next)
// then delegates to auth module.  The auth module's logout() drives the
// Cognito redirect-to-root that brings the user back to /.
// ---------------------------------------------------------------------------
async function doLogout() {
  safeStorage.removeItem(LS_FEED_ID);
  await logout();
}

// ---------------------------------------------------------------------------
// Account deletion (#2588) — Settings → Account → Delete account.
//
// Two explicit steps before the destructive call: the entry button reveals
// a typed-confirmation block, and its final button stays disabled until the
// user types DELETE. On success the server has already removed the Cognito
// user and all account data, so the tail of the flow is just the normal
// logout path (best-effort token revoke + Cognito cookie clear + redirect
// to /), which stands alone inside both store shells — no cross-host hop.
// ---------------------------------------------------------------------------
const DELETE_CONFIRM_PHRASE = 'DELETE';

// ---------------------------------------------------------------------------
// Account deletion copy — what deletion settles for THIS account (#3622).
//
// Written from /me rather than shipped in the markup: the old lead promised
// every account "An active subscription is cancelled immediately", which is
// true only of the one biller `src/account-deletion.ts` actually settles.
// An Apple- or Play-billed subscriber read that, deleted, and kept being
// charged with no account left to cancel from — and no route back, since the
// tombstone drops the store subscription id the RTDN channel routes on.
//
// Rendered on every surface, not just inside a store shell: a Play-billed
// subscriber can delete from a plain browser and is billed just the same.
// And on a FAILED read too (`billingUnknown`), because the Delete button
// does not wait for /me — silence there would reproduce the whole bug on a
// flaky connection.
// ---------------------------------------------------------------------------
function renderAccountDeletionCopy(data, { billingUnknown = false } = {}) {
  if (billingUnknown) {
    // Shown in BOTH steps, unlike the per-provider arms: this is the case
    // where we know least and the user risks most.
    const note = DELETION_BILLING_NOTES.unknown;
    if ($deleteBillingNote) $deleteBillingNote.textContent = note;
    if ($deleteStoreWarning) {
      $deleteStoreWarning.textContent = note;
      $deleteStoreWarning.hidden = false;
    }
    return;
  }
  const { appleBilled, googleBilled, stripeBilled } = deriveBilling(data);
  // Store billing is read live (`paid &&` inside deriveBilling), so a churned
  // store subscriber falls through to the residual arm — correctly: they have
  // nothing left at the store for deletion to leave running.
  const storeNote = appleBilled
    ? DELETION_BILLING_NOTES.apple
    : googleBilled
      ? DELETION_BILLING_NOTES.google
      : '';
  if ($deleteBillingNote) {
    $deleteBillingNote.textContent = storeNote
      || (stripeBilled ? DELETION_BILLING_NOTES.stripe : '');
  }
  if ($deleteStoreWarning) {
    // Only a store subscription outlives the account, so only it earns the
    // second showing at the point of commitment. Stripe's cancellation is
    // reassurance, not a warning — repeating it there would train users to
    // read past the box that matters.
    $deleteStoreWarning.textContent = storeNote;
    $deleteStoreWarning.hidden = storeNote === '';
  }
}

function setupAccountDeletion() {
  const entryBtn   = document.getElementById('delete-account-btn');
  const confirmBox = document.getElementById('delete-account-confirm');
  const input      = document.getElementById('delete-account-input');
  const confirmBtn = document.getElementById('delete-account-confirm-btn');
  const cancelBtn  = document.getElementById('delete-account-cancel-btn');
  if (!entryBtn || !confirmBox || !input || !confirmBtn || !cancelBtn) return;

  entryBtn.addEventListener('click', () => {
    confirmBox.hidden = false;
    entryBtn.hidden = true;
    input.focus();
  });

  cancelBtn.addEventListener('click', () => {
    confirmBox.hidden = true;
    entryBtn.hidden = false;
    input.value = '';
    confirmBtn.disabled = true;
  });

  input.addEventListener('input', () => {
    confirmBtn.disabled = input.value.trim() !== DELETE_CONFIRM_PHRASE;
  });

  const confirmLabel = confirmBtn.textContent;
  confirmBtn.addEventListener('click', async () => {
    confirmBtn.disabled = true;
    cancelBtn.disabled = true;
    confirmBtn.textContent = 'Deleting…';
    try {
      const token = await sessionToken();
      if (!token) throw new Error('Your session expired — sign in and try again.');
      // `retry: false`: the request is destructive and the handler already
      // tolerates a manual retry (it's idempotent server-side), but an
      // ambiguous network failure must surface to the user rather than be
      // blindly resubmitted.
      await apiRequest('/api/account', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }, { retry: false });
      // Account is gone server-side; finish with the standard sign-out so
      // local tokens, caches, and the Cognito hosted-UI cookie all clear.
      await doLogout();
    } catch (err) {
      console.error(err);
      // apiRequest throws `HTTP <status>` with the server's JSON on
      // err.body — surface the handler's wording (e.g. the 502's
      // "nothing was deleted, try again") rather than the bare status.
      let message = 'Account deletion failed — try again.';
      if (err && err.status === 429) {
        message = 'Too many attempts — try again shortly.';
      } else if (err && err.body) {
        try { message = JSON.parse(err.body).error || message; } catch { /* non-JSON body */ }
      } else if (err && !err.status) {
        message = err.message || message;
      }
      showToast(message, 'error');
      confirmBtn.textContent = confirmLabel;
      confirmBtn.disabled = input.value.trim() !== DELETE_CONFIRM_PHRASE;
      cancelBtn.disabled = false;
    }
  });
}

// ---------------------------------------------------------------------------
// Auth state changes — if the user's session is invalidated while they're
// on this page (e.g. a refresh-token revocation racing with a /me call),
// reload so the unauthed boot branch takes over and renders the inline
// sign-in card.  The explicit Sign Out path overrides this almost
// immediately with its own navigation — a redirect to the Cognito logout
// endpoint on web, or a hop to the SPA home inside the Apple app.
// ---------------------------------------------------------------------------
function handleAuthChange() {
  if (isLoggedIn()) return;
  location.reload();
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', async () => {
  // App chrome, not settings content: the wordmark is the way out of this
  // screen whether or not anyone is signed in, so wire it before the auth
  // branch below returns.
  setupHomeLink();

  // Unauthed boot — render the sign-in card in place instead of bouncing
  // through /?next=/settings.  The same card a session that goes mid-visit
  // falls back to, so both live in `showSignInCard`.
  if (!isLoggedIn()) {
    showSignInCard();
    return;
  }

  $feedSubscribe   = document.getElementById('feed-subscribe');
  $feedPlaceholder = document.getElementById('feed-placeholder');
  $logoutBtn       = document.getElementById('logout-btn');
  $voicesCard      = document.getElementById('voices-card');
  $voiceLang       = document.getElementById('voice-lang');
  $voiceList       = document.getElementById('voice-list');
  $mcpCard         = document.getElementById('mcp-card');
  $mcpUrlInput     = document.getElementById('mcp-url-input');
  $appsCard        = document.getElementById('apps-card');
  $appsList        = document.getElementById('apps-list');
  $appsEmpty       = document.getElementById('apps-empty');
  $subscriptionCard       = document.getElementById('subscription-card');
  $subscriptionTierBadge  = document.getElementById('subscription-tier-badge');
  $manageSubscriptionLink = document.getElementById('manage-subscription-link');
  $mobileSubscriptionNote = document.getElementById('mobile-subscription-note');
  $mobileSubscriptionText = document.getElementById('mobile-subscription-text');
  $mobileSubscriptionLink = document.getElementById('mobile-subscription-link');
  $usageBarFill    = document.getElementById('usage-bar-fill');
  $usageBarExtra   = document.getElementById('usage-bar-extra');
  $usageFraction   = document.getElementById('usage-fraction');
  $usageExtra      = document.getElementById('usage-extra');
  $usageReset      = document.getElementById('usage-reset');
  $graceNotice     = document.getElementById('grace-notice');
  $pendingChangeNotice = document.getElementById('pending-change-notice');
  $upgradeCard     = document.getElementById('upgrade-card');
  $upgradeCardIntro = document.getElementById('upgrade-card-intro');
  $upgradeTiers    = document.getElementById('upgrade-tiers');
  $packCard        = document.getElementById('pack-card');
  $packCardNote    = document.getElementById('pack-card-note');
  $packCardTrust   = document.getElementById('pack-card-trust');
  $packCardBiller  = document.getElementById('pack-card-biller');
  $packCardStatus  = document.getElementById('pack-card-status');
  $packAutoOptInRow   = document.getElementById('pack-auto-opt-in-row');
  $packAutoOptIn      = document.getElementById('pack-auto-opt-in');
  $autoTopUpCard      = document.getElementById('auto-topup-card');
  $autoTopUpState     = document.getElementById('auto-topup-state');
  $autoTopUpOff       = document.getElementById('auto-topup-off');
  $autoTopUpLog       = document.getElementById('auto-topup-log');
  $autoTopUpOff?.addEventListener('click', () => disableAutoTopUp());
  $packTiers       = document.getElementById('pack-tiers');
  $applePurchaseCard   = document.getElementById('apple-purchase-card');
  $applePurchaseTiers  = document.getElementById('apple-purchase-tiers');
  $applePurchaseStatus = document.getElementById('apple-purchase-status');
  $applePurchaseNote   = document.getElementById('apple-purchase-note');
  $playPurchaseCard    = document.getElementById('play-purchase-card');
  $playPurchaseTiers   = document.getElementById('play-purchase-tiers');
  $playPurchaseStatus  = document.getElementById('play-purchase-status');
  $playPurchaseNote    = document.getElementById('play-purchase-note');
  $deleteBillingNote   = document.getElementById('delete-account-billing-note');
  $deleteStoreWarning  = document.getElementById('delete-account-store-warning');

  // MCP discovery surface — URL injected at deploy time (issue #874).
  // The card and its nav row ship visible (every stage configures a URL, and
  // the page's first frame must not wait on /config.js to show them —
  // settings/index.html); a deployment without one loses the card here, and
  // setupSettingsNav drops the row with it.
  if (!window.PODSTOW_MCP_URL) {
    if ($mcpCard) $mcpCard.hidden = true;
  } else if ($mcpUrlInput) {
    const mcpUrl = window.PODSTOW_MCP_URL;
    $mcpUrlInput.value = mcpUrl;

    // One-click install deeplinks, shared with the landing /assistants page
    // (clients/shared/mcp-clients.js) so the format-fragile shapes stay in
    // step. OAuth is auto-discovered by the client from the server's 401 +
    // /.well-known/oauth-protected-resource response per the MCP Authorization
    // spec, so neither payload mentions auth.
    const cursorBtn = document.getElementById('add-to-cursor-btn');
    if (cursorBtn) {
      cursorBtn.href = cursorInstallHref(mcpUrl);
      cursorBtn.hidden = false;
    }
    const vscodeBtn = document.getElementById('add-to-vscode-btn');
    if (vscodeBtn) {
      vscodeBtn.href = vscodeInstallHref(mcpUrl);
      vscodeBtn.hidden = false;
    }
  }

  // Render the static "Save articles" method list before the nav is built
  // so the section is fully populated up-front.  Runs after the mcp-card
  // decision above (hidden where no MCP URL is configured) so the AI
  // cross-link can tell whether the AI Assistants section exists here.
  renderSaveMethods();

  // Set up section navigation before data loads so the full sidebar
  // appears immediately rather than items popping in as each fetch
  // completes.  Cards that fetch async data start in data-state=
  // "loading" (see settings/index.html) and flip to "loaded" or
  // "error" from their render functions.
  setupSettingsNav();

  onAuthChange(handleAuthChange);

  // Admin-only nav chrome — revealed only for admins. The bridge debug panel
  // toggle seeds its checkbox from localStorage (default on) and persists any
  // change so the home page picks it up on next load; the analytics link is a
  // plain entry point to the internal dashboard.
  if (isAdmin()) {
    const toggleRow = document.getElementById('bridge-debug-toggle');
    const toggleInput = document.getElementById('bridge-debug-toggle-input');
    if (toggleRow && toggleInput) {
      const stored = safeStorage.getItem(LS_BRIDGE_DEBUG_VISIBLE);
      toggleInput.checked = stored === null ? true : stored === '1';
      toggleRow.hidden = false;
      toggleInput.addEventListener('change', () => {
        safeStorage.setItem(LS_BRIDGE_DEBUG_VISIBLE, toggleInput.checked ? '1' : '0');
      });
    }
    const adminLink = document.getElementById('admin-link');
    if (adminLink) adminLink.hidden = false;
  }

  // Wire up handlers. The feed copy button is owned by the shared subscribe
  // component (rendered in refreshFeedUrl); only the MCP copy stays wired here.
  $logoutBtn?.addEventListener('click', doLogout);
  setupAccountDeletion();
  document.getElementById('copy-mcp-btn')?.addEventListener('click', copyMcpUrl);
  document.getElementById('suggestion-btn')?.addEventListener('click', submitSuggestion);
  $voiceLang?.addEventListener('change', () => {
    selectedVoiceLang = $voiceLang.value;
    renderVoiceList();
  });
  // Initial data load.  Each call is best-effort and independent — a
  // failure (or slowness) on one shouldn't block the others, so all three
  // run concurrently.  Error states inside individual render functions
  // flip the corresponding card to data-state=error so its sidebar entry
  // doesn't lead to a forever-spinning section.
  fetchAccountInfo().catch((err) => {
    // fetchAccountInfo handles its own errors; this catch is a backstop.
    console.error('Failed to load account info:', err);
  });
  loadVoiceSettings().catch((err) => {
    console.error('Failed to load voice settings:', err);
    if ($voicesCard) $voicesCard.dataset.state = 'error';
  });
  renderConnectedApps().catch((err) => {
    console.error('Failed to load connected apps:', err);
    if ($appsCard) $appsCard.dataset.state = 'error';
  });
});

// ---------------------------------------------------------------------------
// Internal exports for testing
// ---------------------------------------------------------------------------
export function _initDomRefs(refs) {
  // A fresh set of refs is a fresh document. A real one starts signed in or it
  // does not reach this module's controls at all; a suite is many documents'
  // worth of renders in one process, and a latch left set would make the next
  // test's session look already gone (#3911).
  _signedOut = false;
  // The same, for the ladder's buttons (#3910). They are only emptied where
  // the ladder is REBUILT, and every arm that withdraws or dead-ends it
  // leaves them in place — so without this the previous document's rungs are
  // what `deadEndPlayLadder` disables, and a test asserting the ladder is
  // dead-ended passes with no rung of its own on screen.
  _playPurchaseButtons = [];
  _playWithdrawalReported = false;
  $feedSubscribe   = refs.feedSubscribe   ?? null;
  $feedPlaceholder = refs.feedPlaceholder ?? $feedPlaceholder;
  if (refs.toastStack) _setToastStack(refs.toastStack);
  $logoutBtn       = refs.logoutBtn       ?? $logoutBtn;
  $voicesCard      = refs.voicesCard      ?? null;
  $voiceLang       = refs.voiceLang       ?? null;
  $voiceList       = refs.voiceList       ?? null;
  $mcpCard         = refs.mcpCard         ?? null;
  $mcpUrlInput     = refs.mcpUrlInput     ?? null;
  $appsCard        = refs.appsCard        ?? null;
  $appsList        = refs.appsList        ?? null;
  $appsEmpty       = refs.appsEmpty       ?? null;
  $subscriptionCard       = refs.subscriptionCard       ?? null;
  $subscriptionTierBadge  = refs.subscriptionTierBadge  ?? null;
  $manageSubscriptionLink = refs.manageSubscriptionLink ?? null;
  $mobileSubscriptionNote = refs.mobileSubscriptionNote ?? null;
  $mobileSubscriptionText = refs.mobileSubscriptionText ?? null;
  $mobileSubscriptionLink = refs.mobileSubscriptionLink ?? null;
  $usageBarFill    = refs.usageBarFill    ?? null;
  $usageBarExtra   = refs.usageBarExtra   ?? null;
  $usageFraction   = refs.usageFraction   ?? null;
  $usageExtra      = refs.usageExtra      ?? null;
  $usageReset      = refs.usageReset      ?? null;
  $graceNotice     = refs.graceNotice     ?? null;
  $pendingChangeNotice = refs.pendingChangeNotice ?? null;
  $upgradeCard     = refs.upgradeCard     ?? null;
  $upgradeCardIntro = refs.upgradeCardIntro ?? null;
  $upgradeTiers    = refs.upgradeTiers    ?? null;
  $packCard        = refs.packCard        ?? null;
  $packCardNote    = refs.packCardNote    ?? null;
  $packCardTrust   = refs.packCardTrust   ?? null;
  $packCardBiller  = refs.packCardBiller  ?? null;
  $packCardStatus  = refs.packCardStatus  ?? null;
  $packAutoOptInRow   = refs.packAutoOptInRow   ?? null;
  $packAutoOptIn      = refs.packAutoOptIn      ?? null;
  $autoTopUpCard      = refs.autoTopUpCard      ?? null;
  $autoTopUpState     = refs.autoTopUpState     ?? null;
  $autoTopUpOff       = refs.autoTopUpOff       ?? null;
  $autoTopUpLog       = refs.autoTopUpLog       ?? null;
  $packTiers       = refs.packTiers       ?? null;
  $applePurchaseCard   = refs.applePurchaseCard   ?? null;
  $applePurchaseTiers  = refs.applePurchaseTiers  ?? null;
  $applePurchaseStatus = refs.applePurchaseStatus ?? null;
  $applePurchaseNote   = refs.applePurchaseNote   ?? null;
  $playPurchaseCard    = refs.playPurchaseCard    ?? null;
  $playPurchaseTiers   = refs.playPurchaseTiers   ?? null;
  $playPurchaseStatus  = refs.playPurchaseStatus  ?? null;
  $playPurchaseNote    = refs.playPurchaseNote    ?? null;
  $deleteBillingNote   = refs.deleteBillingNote   ?? null;
  $deleteStoreWarning  = refs.deleteStoreWarning  ?? null;
}
export { _setRetryDelays as _setApiRetryDelays };
export function _resetShellManage() {
  _stopWaitingForReturn?.();
  _stripeManageOpening = false;
  _stripeManageReturn = null;
}
export function _setAppleManageRefreshDelays(delaysMs) {
  appleManageRefreshDelaysMs = delaysMs ?? APPLE_MANAGE_REFRESH_DELAYS_MS;
}
export function _setPlayPendingDelays(delaysMs) {
  playPendingDelaysMs = delaysMs ?? PLAY_PENDING_DELAYS_MS;
}
export { PLAY_PENDING_DELAYS_MS as _PLAY_PENDING_DELAYS_MS };
export { fetchAccountInfo as _fetchAccountInfo,
         // The only control auto top-up has once it is armed (#3273). Exported
         // because the click listener is wired in the DOMContentLoaded block,
         // which a suite driving `_initDomRefs` never runs — without the seam
         // the DELETE, the re-read and the failure toast are unreachable.
         disableAutoTopUp as _disableAutoTopUp,
         renderConnectedApps as _renderConnectedApps,
         loadVoiceSettings as _loadVoiceSettings,
         maybeEmitPricingView as _maybeEmitPricingView,
         submitSuggestion as _submitSuggestion,
         doLogout as _doLogout,
         renderSubscriptionCard as _renderSubscriptionCard,
         setupSettingsNav as _setupSettingsNav,
         homeEntryToTravelTo as _homeEntryToTravelTo,
         renderSaveMethods as _renderSaveMethods,
         buildStoreBadge as _buildStoreBadge,
         // Re-runs the Play-billing capability probe (#3043). Exported because
         // the probe fires once at module load and caches: a suite that
         // installs `window.getDigitalGoodsService` afterwards would otherwise
         // be testing the answer from before it existed.
         probePlayBilling as _probePlayBilling };
